import React, { useState, useEffect, useRef, useCallback } from 'react';
import ArcadeOverlay from './components/ArcadeOverlay';
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import './messageInputOverrides.css';
import NotificationToast, { Notification } from './components/NotificationToast';
import Editor from '@monaco-editor/react';
import { useAuth } from './contexts/AuthContext';
import RoutesDisplay from './components/RoutesDisplay';
import SecretKeyManager from './components/SecretKeyManager';
import CookiesDashboard from './components/CookiesDashboard';
import TestsDashboard from './components/TestsDashboard';
import LoadingIndicator from './components/LoadingIndicator';
import UsageDashboard from './components/UsageDashboard';
import LoginAutomation from './components/LoginAutomation';
import { AdminPanel } from './components/AdminPanel';
import { ImpersonationBanner } from './components/ImpersonationBanner';
// import UsageDashboard from './components/UsageDashboard';
import FAQPopup from './components/FAQPopup';
import RouteDetailsModal from './components/RouteDetailsModal';
import { config } from './config';
import { smartTruncate, truncateToolResponse } from './utils/truncationUtils';
import { installFetchInterceptor } from './utils/fetchInterceptor';
import { debugAuth } from './utils/debugAuth';
import { supabase } from './lib/supabase';
import { withImpersonationHeader } from './utils/impersonation';

// Function to filter Python code from message content
const filterPythonCode = (content: string): string => {
  if (!config.isProduction()) {
    return content; // Don't filter in development
  }
  
  // Regular expression to match Python code blocks
  // Matches ```python, ```py, or ``` followed by Python-like code
  const pythonCodeBlockRegex = /```(?:python|py)?\s*\n[\s\S]*?```/gi;
  
  // Replace Python code blocks with a placeholder message
  return content.replace(pythonCodeBlockRegex, (match) => {
    // Check if it's explicitly marked as Python
    if (match.toLowerCase().includes('```python') || match.toLowerCase().includes('```py')) {
      return '```\n\n```';
    }
    
    // For unmarked code blocks, check if they contain Python-like syntax
    const codeContent = match.replace(/```/g, '');
    const pythonIndicators = [
      /\bdef\s+\w+\s*\(/,
      /\bclass\s+\w+\s*[:\(]/,
      /\bimport\s+\w+/,
      /\bfrom\s+\w+\s+import/,
      /\bif\s+__name__\s*==\s*['"]__main__['"]/,
      /\bprint\s*\(/,
      /\bfor\s+\w+\s+in\s+/,
      /\bwhile\s+.*:/,
      /\btry\s*:/,
      /\bexcept\s*.*:/,
      /\bwith\s+.*\s+as\s+/,
      /\b(True|False|None)\b/,
      /\b(self|cls)\b/
    ];
    
    const isPython = pythonIndicators.some(regex => regex.test(codeContent));
    if (isPython) {
      return '```\n\n```';
    }
    
    return match; // Keep non-Python code blocks
  });
};

interface ChatMessage {
  id: string;
  content: string;
  sender: string;
  direction: 'incoming' | 'outgoing';
  timestamp: Date;
  messageType?: 'tool_call' | 'tool_response' | 'normal';
}

interface CodeBlock {
  id: string;
  language: string;
  code: string;
  title: string;
  executionResult?: string;
  isExecuting?: boolean;
}

interface FunctionCall {
  function_name: string;
  arguments: Record<string, any>;
  result?: string;
  call_id?: string;
}

interface LiveViewSession {
  session_id: string;
  debug_url: string;
  status: string;
  har_recording: boolean;
}

interface HARFile {
  filename: string;
  size: number;
  created: number;
  modified: number;
  entry_count: number;
}

interface AvailableHARFile {
  filename: string;
  size: number;
  created?: number;
  modified?: number;
  file_id?: string;
}

interface CurrentHARFile {
  filename: string;
  status: 'loaded' | 'loading' | 'error';
  size?: number;
}

interface ChatSession {
  session_id: string;
  updated_at: string;
  message_count: number;
  preview: string;
  last_modified: string;
}

interface SavedContext {
  id: string;
  context_id: string;
  display_name: string;
  context_name?: string | null;
  context?: string | null;
  description?: string | null;
  last_url?: string | null;
  created_at: string;
  updated_at?: string | null;
}

const summarizeContextDetails = (ctx: SavedContext): string | null => {
  const detailCandidate = typeof ctx.context === 'string'
    ? ctx.context
    : typeof ctx.description === 'string'
      ? ctx.description
      : typeof ctx.last_url === 'string'
        ? ctx.last_url
        : null;

  if (!detailCandidate) {
    return null;
  }

  const trimmed = detailCandidate.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed;
};

const App: React.FC = () => {
  const { user, session, signOut, attemptSessionRecovery, impersonatedUserId } = useAuth();
  
  // Stable UID for sticky upstream hashing: prefer auth user id, fallback to local UUID
  const getStableUid = (): string => {
    if (user?.id) return user.id;
    const key = 'integuru_uid';
    let existing = localStorage.getItem(key);
    if (existing) return existing;
    // Lightweight UUID v4 generator
    const uid = ([1e7] as any)+-1e3+-4e3+-8e3+-1e11 as unknown as string;
    const generated = uid.replace(/[018]/g, (c: any) =>
      (Number(c) ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> Number(c) / 4).toString(16)
    );
    localStorage.setItem(key, generated);
    return generated;
  };
  
  // Check if we're on special pages
  const urlParams = new URLSearchParams(window.location.search);
  const isRoutesPage = urlParams.get('page') === 'routes';
  const isLoginAutomationPage = urlParams.get('page') === 'login-automation';
  const isCookiesPage = urlParams.get('page') === 'cookies';
  const isTestsPage = urlParams.get('page') === 'tests';
  const isUsagePage = urlParams.get('page') === 'usage';
  const isAdminPage = urlParams.get('page') === 'admin';
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [wsConnection, setWsConnection] = useState<WebSocket | null>(null);

  // Install fetch interceptor for admin impersonation
  useEffect(() => {
    installFetchInterceptor();
    // Expose debug function globally
    (window as any).debugAuth = debugAuth;
  }, []);

  // Expose WebSocket and test functions for development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).integuruWS = wsConnection;

      // Test functions for session recovery
      (window as any).testSessionRecovery = () => {
        console.log('🧪 Testing session recovery...');

        if (!wsConnection) {
          console.log('❌ No WebSocket connection found - try again when connected');
          return;
        }

        if (wsConnection.readyState !== WebSocket.OPEN) {
          console.log('❌ WebSocket not open - current state:', wsConnection.readyState);
          return;
        }

        console.log('✅ Found WebSocket connection, simulating auth failure...');

        // We can't use 1008 directly (reserved), so we'll simulate the backend response
        // by manually triggering the onClose handler with the auth failure code
        const originalOnClose = wsConnection.onclose;

        // Create a fake CloseEvent with code 1008
        const fakeCloseEvent = {
          code: 1008,
          reason: 'Test: Simulated session expiry',
          wasClean: false,
          type: 'close',
          target: wsConnection,
          currentTarget: wsConnection
        } as unknown as CloseEvent;

        // Trigger the auth failure handling
        if (originalOnClose) {
          console.log('🎭 Simulating server-sent auth failure (code 1008)...');
          originalOnClose.call(wsConnection, fakeCloseEvent);
        } else {
          console.log('❌ No onClose handler found');
        }
      };

      (window as any).testCorruptToken = () => {
        console.log('🧪 Corrupting auth token in localStorage...');

        // Find and corrupt the Supabase auth token
        const authKeys = Object.keys(localStorage).filter(key =>
          key.includes('supabase.auth.token') || key.includes('sb-')
        );

        authKeys.forEach(key => {
          try {
            const authData = JSON.parse(localStorage.getItem(key) || '{}');
            if (authData.access_token) {
              authData.access_token = authData.access_token + '_CORRUPTED_FOR_TEST';
              localStorage.setItem(key, JSON.stringify(authData));
              console.log('✅ Corrupted token in key:', key);
            }
          } catch (e) {
            console.log('⚠️ Could not parse auth data for key:', key);
          }
        });

        console.log('🔄 Now call testSessionRecovery() to trigger the test');
      };

      (window as any).testFullRecoveryFlow = () => {
        console.log('🧪 Starting full session recovery test...');
        (window as any).testCorruptToken();
        setTimeout(() => {
          (window as any).testSessionRecovery();
        }, 500);
      };

      (window as any).testRealAuthFailure = () => {
        console.log('🧪 Testing REAL auth failure by sending message with corrupted token...');

        if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
          console.log('❌ WebSocket not ready');
          return;
        }

        // First corrupt the token
        (window as any).testCorruptToken();

        // Wait a moment for token to be corrupted, then send a message
        // This should trigger backend auth validation and real 1008 response
        setTimeout(() => {
          console.log('📤 Sending message to trigger real backend auth check...');
          wsConnection.send(JSON.stringify({
            type: 'message',
            content: 'Test message to trigger auth validation'
          }));
        }, 1000);
      };
    }
  }, [wsConnection]);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
  
  // Auto-reconnect state
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null); // CRITICAL FIX: Track connection timeout
  const networkRecoveryTimeoutRef = useRef<NodeJS.Timeout | null>(null); // CRITICAL FIX: Track network recovery timeout
  const manualReconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null); // CRITICAL FIX: Track manual reconnect timeout
  const shouldReconnectRef = useRef(true);
  const connectingRef = useRef(false); // RACE CONDITION FIX: Atomic connection guard
  const latestAccessTokenRef = useRef<string | null>(session?.access_token ?? null);
  const activeSocketTokenRef = useRef<string | null>(null);

  useEffect(() => {
    latestAccessTokenRef.current = session?.access_token ?? null;
  }, [session?.access_token]);
  
  // PRODUCTION ROBUSTNESS: Add network status tracking
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const lastConnectedTimeRef = useRef<number>(Date.now());
  const [codeBlocks, setCodeBlocks] = useState<CodeBlock[]>([]);
  const [activeCodeTab, setActiveCodeTab] = useState<string | null>(null);
  const [functionCalls, setFunctionCalls] = useState<FunctionCall[]>([]);
  const [executionResults, setExecutionResults] = useState<Record<string, { output: string; error?: string; timestamp: Date }>>({});
  
  // Live View state
  const [liveViewSession, setLiveViewSession] = useState<LiveViewSession | null>(null);
  const [showArcadeOverlay, setShowArcadeOverlay] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isTerminatingSession, setIsTerminatingSession] = useState(false);
  const [harRecordingStatus, setHarRecordingStatus] = useState<'idle' | 'starting' | 'recording' | 'stopping'>('idle');
  const [loginRecordingStatus, setLoginRecordingStatus] = useState<'idle' | 'starting' | 'recording' | 'stopping'>('idle');
  // When true, hide other controls and show a single "Finish Recording Flow" button
  const [loginFlowModeActive, setLoginFlowModeActive] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  
  // action recording management state
  const [harFiles, setHarFiles] = useState<HARFile[]>([]);
  const [showNamingDialog, setShowNamingDialog] = useState(false);
  const [newHarFileName, setNewHarFileName] = useState('');
  const [lastCreatedHarFile, setLastCreatedHarFile] = useState<string>('');
  const [lastCreatedHarFileId, setLastCreatedHarFileId] = useState<string>('');
  const [isSavingHarFile, setIsSavingHarFile] = useState(false);
  const [isDiscardingHarFile, setIsDiscardingHarFile] = useState(false);
  const [isCancelingContext, setIsCancelingContext] = useState(false);
  const [isSavingContext, setIsSavingContext] = useState(false);
  const [isLoadingHarFiles, setIsLoadingHarFiles] = useState(false);
  
  // HAR selector state
  const [availableHarFiles, setAvailableHarFiles] = useState<AvailableHARFile[]>([]);
  const [currentHarFile, setCurrentHarFile] = useState<CurrentHARFile | null>(null);
  const [isLoadingAvailableFiles, setIsLoadingAvailableFiles] = useState(false);
  const [isSwitchingHarFile, setIsSwitchingHarFile] = useState(false);
  
  // Chat history state
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentChatSession, setCurrentChatSession] = useState<string | null>(null);
  const [isLoadingChatSessions, setIsLoadingChatSessions] = useState(false);
  const [isSwitchingChatSession, setIsSwitchingChatSession] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);

  // Block MessageInput interactions when disabled
  useEffect(() => {
    const blockDisabledInput = (e: Event) => {
      const isDisabled = connectionStatus !== 'connected' || !currentHarFile;
      if (isDisabled) {
        const target = e.target as HTMLElement;
        if (target.closest('.cs-message-input')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }
    };

    if (connectionStatus !== 'connected' || !currentHarFile) {
      // Block all interaction events on the message input when disabled
      document.addEventListener('keydown', blockDisabledInput, true);
      document.addEventListener('keypress', blockDisabledInput, true);
      document.addEventListener('input', blockDisabledInput, true);
      document.addEventListener('paste', blockDisabledInput, true);
      document.addEventListener('focus', blockDisabledInput, true);
    }

    return () => {
      document.removeEventListener('keydown', blockDisabledInput, true);
      document.removeEventListener('keypress', blockDisabledInput, true);
      document.removeEventListener('input', blockDisabledInput, true);
      document.removeEventListener('paste', blockDisabledInput, true);
      document.removeEventListener('focus', blockDisabledInput, true);
    };
  }, [connectionStatus, currentHarFile]);
  
  // Layout state
  const [chatWidth, setChatWidth] = useState(40); // percentage
  const [isDragging, setIsDragging] = useState(false);
  
  // Notification state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  
  // Route popup state
  const [showRoutePopup, setShowRoutePopup] = useState(false);
  const [showFAQPopup, setShowFAQPopup] = useState(false);
  const [routePopupData, setRoutePopupData] = useState<{
    route_info: any;
    curl_examples: any;
  } | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartChatWidth, setDragStartChatWidth] = useState(0);
  
  // Removed tab state - now showing live view only
  
  // JS call frames toggle state  
  const [includeJsCallFrames, setIncludeJsCallFrames] = useState(true);
  
  // User dropdown and secret key manager state
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSecretKeyManager, setShowSecretKeyManager] = useState(false);
  
  // Context management state
  const [showContextNamingDialog, setShowContextNamingDialog] = useState(false);
  // Prompt shown after saving a context to optionally record a login flow
  const [loginFlowPrompt, setLoginFlowPrompt] = useState<{ contextId: string; displayName: string } | null>(null);
  const [contextToSave, setContextToSave] = useState<{sessionId: string, contextId: string, currentUrl?: string} | null>(null);
  const [newContextName, setNewContextName] = useState('');
  const [savedContexts, setSavedContexts] = useState<any[]>([]);
  const [showContextDropdown, setShowContextDropdown] = useState(false);
  
  // Track if current session was created from existing saved context
  const [isExistingContext, setIsExistingContext] = useState<{contextId: string, displayName: string} | null>(null);
  
  // HAR dropdown state
  const [showHarDropdown, setShowHarDropdown] = useState(false);
  const [editingHarFile, setEditingHarFile] = useState<string | null>(null);
  const [editHarFileName, setEditHarFileName] = useState('');
  const [deleteConfirmFile, setDeleteConfirmFile] = useState<string | null>(null);

  const currentResponseRef = useRef<string>('');
  const messageIdRef = useRef<number>(0);
  const isLoadingDataRef = useRef<boolean>(false);
  const responseBeepRef = useRef<HTMLAudioElement | null>(null);
  const chatHistoryRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const harDropdownRef = useRef<HTMLDivElement>(null);
  const contextDropdownRef = useRef<HTMLDivElement>(null);
  const welcomeMessageAddedRef = useRef<boolean>(false);
  const contextButtonRef = useRef<HTMLButtonElement>(null);
  const previousImpersonatedUserIdRef = useRef<string | null>(impersonatedUserId || null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    responseBeepRef.current = new Audio('/sounds/chat-response-beep.wav');
    responseBeepRef.current.volume = 0.15;
    responseBeepRef.current.preload = 'auto';

    return () => {
      if (responseBeepRef.current) {
        responseBeepRef.current.pause();
        responseBeepRef.current = null;
      }
    };
  }, []);

  // Close chat history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (chatHistoryRef.current && !chatHistoryRef.current.contains(event.target as Node)) {
        setShowChatHistory(false);
      }
    };

    if (showChatHistory) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showChatHistory]);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserDropdown]);

  // Close HAR dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (harDropdownRef.current && !harDropdownRef.current.contains(event.target as Node)) {
        setShowHarDropdown(false);
        setEditingHarFile(null);
        setEditHarFileName('');
      }
    };

    if (showHarDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showHarDropdown]);

  // Close context dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextDropdownRef.current && !contextDropdownRef.current.contains(event.target as Node)) {
        setShowContextDropdown(false);
      }
    };

    if (showContextDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showContextDropdown]);

  // Sign out handler
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Active tab detection for notification isolation
  const [isActiveTab, setIsActiveTab] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsActiveTab(!document.hidden);
    };

    const handleTestNotification = (event: any) => {
      const { title, message, type } = event.detail;
      addSystemNotification(title, message, type);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('testNotification', handleTestNotification);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('testNotification', handleTestNotification);
    };
  }, [isActiveTab]);

  // Notification management functions
  const addNotification = (title: string, message: string = '', type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    const notification: Notification = {
      id: Date.now().toString(),
      title,
      message,
      type,
      timestamp: Date.now()
    };

    setNotifications(prev => [notification, ...prev]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // action recording management functions
  const startEditingHarFile = (filename: string) => {
    setEditingHarFile(filename);
    setEditHarFileName(filename.replace('.har', ''));
  };

  const cancelEditingHarFile = () => {
    setEditingHarFile(null);
    setEditHarFileName('');
  };

  const saveHarFileName = async () => {
    if (!editingHarFile || !editHarFileName.trim() || !session?.access_token) return;

    const newFilename = editHarFileName.trim() + '.har';
    if (newFilename === editingHarFile) {
      cancelEditingHarFile();
      return;
    }

    try {
      const response = await fetch(config.getApiUrl('/browser/har-files/rename'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          old_filename: editingHarFile,
          new_filename: newFilename
        })
      });

      if (response.ok) {
        addSystemNotification('Action Recording Renamed', `Successfully renamed to: ${newFilename}`, 'success');
        await loadAvailableHarFiles();
        
        // Update current file if it was the one being renamed
        if (currentHarFile?.filename === editingHarFile) {
          setCurrentHarFile(prev => prev ? {...prev, filename: newFilename} : null);
        }
        
        cancelEditingHarFile();
      } else {
        const errorData = await response.json();
        addSystemNotification('Rename Failed', `Failed to rename action recording: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error renaming action recording:', error);
      addSystemNotification('Rename Error', `Error renaming action recording: ${error}`, 'error');
    } finally {
      setIsSavingHarFile(false);
    }
  };

  // Login flow recorder controls
  const startLoginRecorder = async () => {
    if (!session?.access_token || !liveViewSession) return;
    setLoginRecordingStatus('starting');
    try {
      const res = await fetch(config.getApiUrl(`/browser/session/${liveViewSession.session_id}/login-recorder/start`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const txt = await res.text();
        addSystemNotification('Failed to start login recorder', txt || `${res.status}`, 'error');
        setLoginRecordingStatus('idle');
        return;
      }
      setLoginRecordingStatus('recording');
      addSystemNotification('Login Recorder Started', 'Begin performing your login flow in the live browser.', 'success');
    } catch (e) {
      console.error('Error starting login recorder', e);
      addSystemNotification('Error starting login recorder', String(e), 'error');
      setLoginRecordingStatus('idle');
    }
  };

  const stopLoginRecorder = async () => {
    if (!session?.access_token || !liveViewSession) return;
    setLoginRecordingStatus('stopping');
    try {
      const res = await fetch(config.getApiUrl(`/browser/session/${liveViewSession.session_id}/login-recorder/stop`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const txt = await res.text();
        addSystemNotification('Failed to stop login recorder', txt || `${res.status}`, 'error');
        setLoginRecordingStatus('recording');
        return;
      }
      const data = await res.json();
      setLoginRecordingStatus('idle');
      const savedWhere = data.uploaded && data.s3_key ? `Saved to S3: ${data.s3_key}` : 'Returned code in response.';
      addSystemNotification('Login Recorder Saved', `${data.actions_count} action(s). ${savedWhere}`, 'success');
      // Optionally, expose code in console for developer
      try { console.log('Recorded login actions code:', data.code); } catch {}
    } catch (e) {
      console.error('Error stopping login recorder', e);
      addSystemNotification('Error stopping login recorder', String(e), 'error');
      setLoginRecordingStatus('recording');
    }
  };

  const deleteHarFile = async (filename: string) => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(config.getApiUrl('/browser/har-files/delete'), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ filename })
      });

      if (response.ok) {
        addSystemNotification('Action Recording Deleted', `Successfully deleted: ${filename}`, 'success');
        await loadAvailableHarFiles();
        
        // Clear current file if it was the one being deleted
        if (currentHarFile?.filename === filename) {
          setCurrentHarFile(null);
        }
        
        setDeleteConfirmFile(null);
      } else {
        const errorData = await response.json();
        addSystemNotification('Delete Failed', `Failed to delete action recording: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting action recording:', error);
      addSystemNotification('Delete Error', `Error deleting action recording: ${error}`, 'error');
    }
  };

  // Suppress ResizeObserver errors at the global level
  useEffect(() => {
    // This is the most effective way to suppress ResizeObserver errors
    const resizeObserverErr = window.ResizeObserver;
    window.ResizeObserver = class extends resizeObserverErr {
      constructor(callback: ResizeObserverCallback) {
        const wrappedCallback: ResizeObserverCallback = (entries, observer) => {
          window.requestAnimationFrame(() => {
            callback(entries, observer);
          });
        };
        super(wrappedCallback);
      }
    };

    return () => {
      window.ResizeObserver = resizeObserverErr;
    };
  }, []);

  // Track which data has been loaded to avoid redundant fetches
  const [dataLoadState, setDataLoadState] = useState({
    harFiles: false,
    chatSessions: false,
    savedContexts: false
  });

  // Auto-open tutorial once per browser using a single localStorage key
  useEffect(() => {
    try {
      const key = 'tutorial_seen_v1';
      const forceShow = new URLSearchParams(window.location.search).get('show_tutorial') === '1';
      if (forceShow || !localStorage.getItem(key)) {
        setShowArcadeOverlay(true);
        localStorage.setItem(key, 'true');
      }
    } catch (_) {
      // Best-effort only
    }
  }, []);

  // Only load chat sessions when chat history dropdown is opened
  useEffect(() => {
    if (showChatHistory && session?.access_token && !dataLoadState.chatSessions) {
      loadChatSessions();
      setDataLoadState(prev => ({ ...prev, chatSessions: true }));
    }
  }, [showChatHistory, session?.access_token]);

  // Load available action recordings when user is authenticated (but not on special pages for performance)
  useEffect(() => {
    if (session?.access_token && !dataLoadState.harFiles) {
      // Skip loading HAR files on special pages to improve performance
      const urlParams = new URLSearchParams(window.location.search);
      const currentPage = urlParams.get('page');
      const isSpecialPage = ['usage', 'routes', 'cookies', 'tests'].includes(currentPage || '');
      
      if (!isSpecialPage) {
        loadAvailableHarFiles();
        setDataLoadState(prev => ({ ...prev, harFiles: true }));
      }
    }
  }, [session?.access_token]);

  // Load additional HAR data when dropdown is opened
  useEffect(() => {
    if (showHarDropdown && session?.access_token) {
      Promise.all([
        loadHarFiles(),
        loadCurrentHarFileInfo()
      ]);
    }
  }, [showHarDropdown, session?.access_token]);

  // Only load saved contexts when context dropdown is opened
  useEffect(() => {
    if (showContextDropdown && session?.access_token && !dataLoadState.savedContexts) {
      loadSavedContexts();
      setDataLoadState(prev => ({ ...prev, savedContexts: true }));
    }
  }, [showContextDropdown, session?.access_token]);

  // Auto-cleanup: Terminate HyperBrowser live view session on tab close/refresh
  useEffect(() => {
    const terminateSessionOnExit = (sessionId: string, accessToken: string) => {
      const terminateUrl = config.getApiUrl(`/browser/session/${sessionId}/terminate`);
      
      // Use fetch with keepalive as primary method (more reliable for auth headers)
      fetch(terminateUrl, {
        method: 'POST',
        headers: getAuthHeaders(accessToken),
        keepalive: true
      }).then(response => {
        if (response.ok) {
          console.log('✅ Successfully terminated HyperBrowser session:', sessionId);
        } else {
          console.error('❌ Failed to terminate session:', response.status, response.statusText);
        }
      }).catch(error => {
        console.error('❌ Error terminating session:', error);
        
        // Fallback to sendBeacon without auth headers (less reliable but worth trying)
        const beaconData = new FormData();
        beaconData.append('method', 'POST');
        const success = navigator.sendBeacon(terminateUrl, beaconData);
        console.log('📡 Beacon fallback result:', success);
      });
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (liveViewSession && session?.access_token) {
        console.log('🔄 Auto-terminating HyperBrowser session due to page unload:', liveViewSession.session_id);
        terminateSessionOnExit(liveViewSession.session_id, session.access_token);
      }
    };

    // Only use beforeunload to detect actual tab closing/navigation
    // Removed visibilitychange to prevent termination on tab switching
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [liveViewSession, session?.access_token]);
 
  // Component unmount cleanup: Terminate active session
  useEffect(() => {
    // Cleanup function that runs on component unmount
    return () => {
      if (liveViewSession && session?.access_token) {
        console.log('🧹 Cleaning up HyperBrowser session on component unmount:', liveViewSession.session_id);
        
        const terminateUrl = config.getApiUrl(`/browser/session/${liveViewSession.session_id}/terminate`);
        
        // Use the same termination logic as the other cleanup
        fetch(terminateUrl, {
          method: 'POST',
          headers: getAuthHeaders(session.access_token),
          keepalive: true
        }).then(response => {
          if (response.ok) {
            console.log('✅ Successfully terminated session on unmount:', liveViewSession.session_id);
          } else {
            console.error('❌ Failed to terminate session on unmount:', response.status);
          }
        }).catch(error => {
          console.error('❌ Error terminating session on unmount:', error);
        });
      }
    };
  }, [liveViewSession?.session_id, session?.access_token]);

  // Helper function to get auth headers
  const getAuthHeaders = (overrideToken?: string): HeadersInit => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = overrideToken ?? latestAccessTokenRef.current ?? session?.access_token ?? undefined;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return withImpersonationHeader(headers);
  };

  const loadHarFiles = async () => {
    if (!session?.access_token) return;
    
    setIsLoadingHarFiles(true);
    try {
      const response = await fetch(config.getApiUrl('/browser/har-files'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setHarFiles(data.har_files || []);
      } else if (response.status === 401) {
        console.error('Authentication failed - please log in again');
        // Handle auth failure - could redirect to login
      } else {
        console.error('Failed to load action recordings:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading action recordings:', error);
    } finally {
      setIsLoadingHarFiles(false);
    }
  };

  const renameHarFile = async () => {
    console.log('renameHarFile called with:', { 
      newHarFileName: newHarFileName, 
      lastCreatedHarFile: lastCreatedHarFile, 
      hasAccessToken: !!session?.access_token 
    });
    
    if (!newHarFileName.trim() || !lastCreatedHarFile || !session?.access_token || isSavingHarFile) {
      console.log('Validation failed:', {
        hasNewFileName: !!newHarFileName.trim(),
        hasLastCreatedFile: !!lastCreatedHarFile,
        hasAccessToken: !!session?.access_token,
        isSaving: isSavingHarFile
      });
      return;
    }
    
    setIsSavingHarFile(true);
    try {
      const response = await fetch(config.getApiUrl('/browser/har-files/rename'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          old_filename: lastCreatedHarFile,
          new_filename: newHarFileName.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        addSystemNotification(`action recording renamed to: ${result.new_filename}`, 'success');
        
        // Refresh the file list
        await loadHarFiles();
        
        // Close dialog and reset state
        setShowNamingDialog(false);
        setNewHarFileName('');
        setLastCreatedHarFile('');
        setLastCreatedHarFileId('');
      } else if (response.status === 401) {
        addSystemNotification('Authentication Required', 'Please log in again to continue', 'error');
      } else {
        const errorData = await response.json();
        addSystemNotification('Rename Failed', `Failed to rename action recording: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error renaming action recording:', error);
      addSystemNotification('Rename Error', `Error renaming action recording: ${error}`, 'error');
    } finally {
      setIsSavingHarFile(false);
    }
  };

  const cancelNamingDialog = async () => {
    setIsDiscardingHarFile(true);
    
    try {
      // If there's a action recording that was just created, delete it since user cancelled
      if (lastCreatedHarFile && lastCreatedHarFileId) {
        console.log('🗑️ Cancelling action recording creation, deleting:', lastCreatedHarFile, 'with ID:', lastCreatedHarFileId);

        const response = await fetch(config.getApiUrl(`/browser/har-files/${lastCreatedHarFileId}`), {
          method: 'DELETE',
          headers: getAuthHeaders()
        });

        if (response.ok) {
          console.log('✅ Successfully deleted action recording:', lastCreatedHarFile);
          addSystemNotification('Recording cancelled - file discarded', 'info');
          // Refresh the action recordings list
          await loadHarFiles();
        } else {
          const errorText = await response.text();
          console.error('❌ Failed to delete cancelled action recording:', errorText);
          addSystemNotification(`Failed to discard recording: ${errorText}`, 'error');
          return; // Don't close dialog on error
        }
      } else {
        console.warn('⚠️ No action recording to delete. File:', lastCreatedHarFile, 'ID:', lastCreatedHarFileId);
      }
    } catch (error) {
      console.error('❌ Error deleting cancelled action recording:', error);
      addSystemNotification(`Error discarding recording: ${error}`, 'error');
      return; // Don't close dialog on error
    } finally {
      setIsDiscardingHarFile(false);
    }

    setShowNamingDialog(false);
    setNewHarFileName('');
    setLastCreatedHarFile('');
    setLastCreatedHarFileId('');
    setIsSavingHarFile(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  // Context management functions
  const loadSavedContexts = async () => {
    if (!session?.access_token) return;
    
    try {
      const response = await fetch(config.getApiUrl('/browser/contexts'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSavedContexts(data.contexts || []);
      } else {
        console.error('Failed to load saved contexts:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading saved contexts:', error);
    }
  };

  const cleanupUnsavedContext = async () => {
    // Clean up the unsaved context from database
    if (contextToSave && session?.access_token) {
      try {
        await fetch(config.getApiUrl(`/browser/contexts/${contextToSave.contextId}`), {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
      } catch (error) {
        console.error('Error cleaning up context:', error);
      }
    }
  };

  const cancelContextSaving = async () => {
    setIsCancelingContext(true);
    
    try {
      await cleanupUnsavedContext();
      setShowContextNamingDialog(false);
      setNewContextName('');
      setContextToSave(null);
    } catch (error) {
      console.error('Error during context cancellation:', error);
    } finally {
      setIsCancelingContext(false);
    }
  };

  const saveContext = async () => {
    if (!contextToSave || !newContextName.trim() || !session?.access_token || isSavingContext) return;

    setIsSavingContext(true);
    
    try {
      const response = await fetch(config.getApiUrl('/browser/contexts'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          context_id: contextToSave.contextId,
          display_name: newContextName.trim(),
          description: `Saved from session ${contextToSave.sessionId.slice(0, 8)}`,
          last_url: contextToSave.currentUrl
        })
      });

      if (response.ok) {
        const data = await response.json();
        addSystemNotification(`Context saved as: ${newContextName.trim()}`, 'success');
        await loadSavedContexts();
        // Close naming dialog
        setShowContextNamingDialog(false);
        // Queue follow-up prompt to record login flow using this saved context
        try {
          const saved = data?.context;
          const ctxId = saved?.context_id || contextToSave.contextId;
          const disp = saved?.display_name || newContextName.trim();
          if (ctxId && disp) {
            setLoginFlowPrompt({ contextId: ctxId, displayName: disp });
          }
        } catch (_) { /* best-effort */ }
        // Reset input state
        setNewContextName('');
        setContextToSave(null);
      } else {
        const errorData = await response.json();
        addSystemNotification(`Failed to save context: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error saving context:', error);
      addSystemNotification(`Error saving context: ${error}`, 'error');
    } finally {
      setIsSavingContext(false);
    }
  };

  // Helper to spawn a new session with a saved context and begin login recorder
  const startLoginFlowForContext = async (contextId: string, displayName: string) => {
    if (!conversationId) {
      addSystemNotification('No conversation active to attach session.', 'error');
      return;
    }
    const created = await createSessionWithContext(contextId, displayName);
    if (!created) return;
    try {
      setLoginRecordingStatus('starting');
      const res = await fetch(config.getApiUrl(`/browser/session/${created.session_id}/login-recorder/start`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const txt = await res.text();
        addSystemNotification('Failed to start login recorder', txt || `${res.status}`, 'error');
        setLoginRecordingStatus('idle');
        return;
      }
      setLoginRecordingStatus('recording');
      setLoginFlowModeActive(true);
      addSystemNotification('Login Recorder Started', 'Begin performing your login flow in the live browser.', 'success');
    } catch (e) {
      console.error('Error starting login recorder', e);
      addSystemNotification('Error starting login recorder', String(e), 'error');
      setLoginRecordingStatus('idle');
    }
  };

  // Finish button: stop recorder, save script, and fully terminate the browser session
  const finishLoginFlow = async () => {
    if (!session?.access_token || !liveViewSession) return;
    try {
      setLoginRecordingStatus('stopping');
      // Stop recorder and save script
      const stopRes = await fetch(config.getApiUrl(`/browser/session/${liveViewSession.session_id}/login-recorder/stop`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!stopRes.ok) {
        const txt = await stopRes.text();
        addSystemNotification('Failed to stop login recorder', txt || `${stopRes.status}`, 'error');
        setLoginRecordingStatus('recording');
        return;
      }
      const data = await stopRes.json();
      const savedWhere = data.uploaded && data.s3_key ? `Saved to S3: ${data.s3_key}` : 'Returned code in response.';
      addSystemNotification('Login Recorder Saved', `${data.actions_count} action(s). ${savedWhere}`, 'success');

      // Terminate session (fully close browser)
      const delRes = await fetch(config.getApiUrl(`/browser/session/${liveViewSession.session_id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!delRes.ok) {
        const t = await delRes.text();
        addSystemNotification('Failed to close browser session', t || `${delRes.status}`, 'warning');
      } else {
        addSystemNotification('Browser Closed', 'Login flow recording finished and browser closed.', 'success');
      }

      // Reset UI state
      setLiveViewSession(null);
      setLoginFlowModeActive(false);
      setLoginRecordingStatus('idle');
    } catch (e) {
      console.error('Error finishing login flow', e);
      addSystemNotification('Error finishing login flow', String(e), 'error');
      setLoginRecordingStatus('recording');
    }
  };

  const updateExistingContext = async (contextId: string, currentUrl: string, displayName: string) => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(config.getApiUrl('/browser/contexts'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          context_id: contextId,
          display_name: displayName,
          last_url: currentUrl
        })
      });

      if (response.ok) {
        addSystemNotification(`Context "${displayName}" updated with current URL`, 'success');
        await loadSavedContexts();
      } else {
        console.error('Failed to update existing context');
        addSystemNotification(`Failed to update context URL`, 'warning');
      }
    } catch (error) {
      console.error('Error updating existing context:', error);
      addSystemNotification(`Error updating context: ${error}`, 'error');
    }
  };

  const createSessionWithContext = async (contextId: string, displayName: string): Promise<{ session_id: string; debug_url: string } | null> => {
    if (!session?.access_token || !conversationId) {
      addSystemNotification('Authentication required or no conversation ID available.', 'error');
      return null;
    }
    
    setIsCreatingSession(true);
    try {
      const response = await fetch(config.getApiUrl('/browser/session'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          conversation_id: conversationId,
          context_id: contextId
        })
      });

      if (response.ok) {
        const result = await response.json();
        setLiveViewSession({
          session_id: result.session_id,
          debug_url: result.debug_url,
          status: 'active',
          har_recording: false
        });
        
        // Track that this session was created from an existing saved context
        setIsExistingContext({
          contextId: contextId,
          displayName: displayName
        });
        
        addSystemNotification(`Live view session created with context "${displayName}"!`, 'success');
        
        setShowContextDropdown(false);
        return { session_id: result.session_id, debug_url: result.debug_url };
      } else {
        const errorText = await response.text();
        addSystemNotification(`Failed to create session with context: ${errorText}`, 'error');
        return null;
      }
    } catch (error) {
      console.error('Error creating session with context:', error);
      addSystemNotification(`Error creating session with context: ${error}`, 'error');
      return null;
    } finally {
      setIsCreatingSession(false);
    }
  };

  const loadCurrentHarFileInfo = async () => {
    if (!session?.access_token) return;
    
    try {
      const response = await fetch(config.getApiUrl('/browser/current-har-file'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.current_file) {
          setCurrentHarFile({
            filename: data.current_file.filename,
            status: 'loaded',
            size: data.current_file.size
          });
        } else {
          setCurrentHarFile(null);
        }
      } else if (response.status === 401) {
        console.error('Authentication failed loading current action recording');
      }
    } catch (error) {
      console.error('Error loading current action recording info:', error);
    }
  };

  const loadAvailableHarFiles = async () => {
    if (!session?.access_token) return;
    
    setIsLoadingAvailableFiles(true);
    try {
      const response = await fetch(config.getApiUrl('/browser/available-har-files'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableHarFiles(data.files || []);
      } else {
        console.error(`Failed to load action recordings: ${response.status} ${response.statusText}`);
        // Set empty array to ensure dropdown remains enabled for retry
        setAvailableHarFiles([]);
        if (response.status === 401) {
          console.error('Authentication failed loading available action recordings');
        }
      }
    } catch (error) {
      console.error('Error loading available action recordings:', error);
      // Set empty array to ensure dropdown remains enabled for retry
      setAvailableHarFiles([]);
    } finally {
      setIsLoadingAvailableFiles(false);
    }
  };

  const switchHarFile = async (filename: string) => {
    if (!session?.access_token) return;
    
    setIsSwitchingHarFile(true);
    try {
      const response = await fetch(config.getApiUrl(`/browser/switch-har-file?uid=${encodeURIComponent(getStableUid())}`), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ filename })
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentHarFile({
          filename: result.filename,
          status: 'loaded'
        });
        
        addSystemNotification(`Switched to action recording: ${result.filename} (${result.request_count} requests)`, 'success');
        
        // Refresh available files list
        await loadAvailableHarFiles();
      } else if (response.status === 401) {
        addSystemNotification(`Authentication required. Please log in again.`, 'error');
      } else {
        const errorData = await response.json();
        addSystemNotification(`Failed to switch action recording: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error switching action recording:', error);
      addSystemNotification(`Error switching action recording: ${error}`, 'error');
    } finally {
      setIsSwitchingHarFile(false);
    }
  };

  // Chat history functions
  const loadChatSessions = async () => {
    if (!session?.access_token) return;
    
    setIsLoadingChatSessions(true);
    try {
      const response = await fetch(config.getApiUrl('/chat/sessions'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setChatSessions(data.sessions || []);
      } else if (response.status === 401) {
        console.error('Authentication failed - please log in again');
      } else {
        console.error('Failed to load chat sessions:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    } finally {
      setIsLoadingChatSessions(false);
    }
  };

  const switchChatSession = async (sessionId: string) => {
    if (!session?.access_token) return;
    
    setIsSwitchingChatSession(true);
    try {
      const response = await fetch(config.getApiUrl(`/chat/sessions/${sessionId}`), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const sessionData = await response.json();
        
        // Clear current messages and load the historical chat
        setMessages([]);
        
        // Convert session messages to chat messages
        const historicalMessages: ChatMessage[] = sessionData.messages.map((msg: any, index: number) => ({
          id: msg.id || `historical-${index}`,
          content: msg.content,
          sender: msg.sender,
          direction: msg.direction,
          timestamp: new Date(msg.timestamp),
          messageType: msg.message_type || 'normal'
        }));
        
        setMessages(historicalMessages);
        setCurrentChatSession(sessionId);
        
        // Send WebSocket message to restore conversation context in AI
        if (wsConnection?.readyState === WebSocket.OPEN) {
          wsConnection.send(JSON.stringify({
            type: 'restore_session',
            session_id: sessionId
          }));
        }
        
        addSystemNotification(`Loaded chat session with ${sessionData.message_count} messages`, 'success');
        
        // Close the chat history dropdown
        setShowChatHistory(false);
      } else if (response.status === 401) {
        addSystemNotification(`Authentication required. Please log in again.`, 'error');
      } else {
        const errorData = await response.json();
        addSystemNotification(`Failed to load chat session: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error switching chat session:', error);
      addSystemNotification(`Error loading chat session: ${error}`, 'error');
    } finally {
      setIsSwitchingChatSession(false);
    }
  };

  const startNewChatSession = async () => {
    if (!session?.access_token) return;
    
    try {
      const response = await fetch(config.getApiUrl('/chat/sessions/new'), {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        
        // Clear current messages and set new session
        setMessages([]);
        setCurrentChatSession(data.session_id);
        // Immediately show the default prompt for new chats
        addMessage('What is the action you want to integrate?', 'assistant', 'incoming');
        // Reset the welcome message flag so initial-connect logic stays consistent
        welcomeMessageAddedRef.current = true;
        
        // Tell WebSocket to start a new conversation session
        if (wsConnection?.readyState === WebSocket.OPEN) {
          wsConnection.send(JSON.stringify({
            type: 'start_new_session'
          }));
        }
        
        // Refresh chat sessions list to include the new one
        loadChatSessions();
        
        addSystemNotification(`Started new chat session`, 'success');
        
        // Close the chat history dropdown
        setShowChatHistory(false);
      } else {
        console.error('Failed to create new chat session:', response.statusText);
        addSystemNotification(`Failed to create new chat session`, 'error');
      }
    } catch (error) {
      console.error('Error creating new chat session:', error);
      addSystemNotification(`Error creating new chat session: ${error}`, 'error');
    }
  };

  // Initialize with a sample code block
  useEffect(() => {
    const sampleCodeBlock: CodeBlock = {
      id: `code-sample-${Date.now()}`,
      language: 'python',
      code: `# Welcome to the Python Code Editor!
# Edit this code and click "Run Code" to execute it

print("Hello, World!")

# Try some basic Python operations
numbers = [1, 2, 3, 4, 5]
print(f"Numbers: {numbers}")

# Calculate sum and average
total = sum(numbers)
average = total / len(numbers)

print(f"Sum: {total}")
print(f"Average: {average}")

# You can replace this code with your own!`,
      title: 'Sample Python Code'
    };

    setCodeBlocks([sampleCodeBlock]);
    setActiveCodeTab(sampleCodeBlock.id);
  }, []); // Empty dependency array means this runs once on mount

  const scrollToBottom = () => {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      // Try to find the message list container in the DOM
      const messageListContainer = document.querySelector('.cs-message-list');
      if (messageListContainer) {
        messageListContainer.scrollTop = messageListContainer.scrollHeight;
      } else {
        // Fallback: try other selectors
        const containers = [
          '.cs-message-list__scroll-container',
          '.cs-chat-container .cs-message-list',
          '[data-testid="message-list"]'
        ];
        
        for (const selector of containers) {
          const container = document.querySelector(selector);
          if (container) {
            container.scrollTop = container.scrollHeight;
            break;
          }
        }
      }
    }, 50);
  };

  // Initialize WebSocket connection when authenticated - reconnect when token changes
  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Disable auto-reconnect on unmount
      shouldReconnectRef.current = false;
      
      // Clear any pending timeouts - CRITICAL FIX: Include all timeout types
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      if (networkRecoveryTimeoutRef.current) {
        clearTimeout(networkRecoveryTimeoutRef.current);
      }
      if (manualReconnectTimeoutRef.current) {
        clearTimeout(manualReconnectTimeoutRef.current);
      }
      
      // Close WebSocket connection
      if (wsConnection) {
        wsConnection.close();
      }
    };
  }, []);

  // PRODUCTION ROBUSTNESS: Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network back online');
      setIsOnline(true);
      
      // If we were disconnected and network is back, attempt reconnection
      // CRITICAL FIX: Use ref to avoid stale closure - check connection state dynamically
      if (shouldReconnectRef.current) {
        console.log('Network restored, attempting reconnection...');
        reconnectAttemptsRef.current = 0; // Reset attempts on network recovery
        // Always attempt reconnection - connectWebSocket will handle duplicate connections
        networkRecoveryTimeoutRef.current = setTimeout(connectWebSocket, 1000);
      }
    };

    const handleOffline = () => {
      console.log('Network went offline');
      setIsOnline(false);
      
      // Don't attempt reconnections while offline - clear ALL pending timeouts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (networkRecoveryTimeoutRef.current) {
        clearTimeout(networkRecoveryTimeoutRef.current);
        networkRecoveryTimeoutRef.current = null;
      }
      if (manualReconnectTimeoutRef.current) {
        clearTimeout(manualReconnectTimeoutRef.current);
        manualReconnectTimeoutRef.current = null;
      }
      
      addSystemNotification('Network Offline', 'Connection will resume when network is restored', 'warning');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connectionStatus]);

  // Auto-reconnect function with exponential backoff
  const scheduleReconnect = () => {
    if (!shouldReconnectRef.current || reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('Max reconnect attempts reached or reconnection disabled');
      return;
    }

    // PRODUCTION ROBUSTNESS: Don't attempt reconnection while offline
    if (!isOnline) {
      console.log('Skipping reconnection attempt - network is offline');
      return;
    }

    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s (max 30s)
    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
    
    console.log(`Scheduling reconnect attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts} in ${delay}ms`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      connectWebSocket();
    }, delay);
  };

  const fetchFreshAccessToken = useCallback(async (): Promise<string | null> => {
    const fallbackToken = session?.access_token ?? latestAccessTokenRef.current ?? null;

    if (!supabase) {
      latestAccessTokenRef.current = fallbackToken;
      return fallbackToken;
    }

    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Failed to fetch session for WebSocket auth:', error);
        latestAccessTokenRef.current = fallbackToken;
        return fallbackToken;
      }

      const freshToken = data.session?.access_token ?? fallbackToken;
      latestAccessTokenRef.current = freshToken;
      return freshToken ?? null;
    } catch (error) {
      console.error('Error while fetching session for WebSocket auth:', error);
      latestAccessTokenRef.current = fallbackToken;
      return fallbackToken;
    }
  }, [session?.access_token]);


  const connectWebSocket = useCallback(async () => {
    if (wsConnection && (wsConnection.readyState === WebSocket.OPEN || wsConnection.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket already active, skipping new connection attempt');
      return;
    }

    if (connectingRef.current) {
      console.log('Connection attempt already in progress, skipping...');
      return;
    }

    connectingRef.current = true;

    const accessToken = await fetchFreshAccessToken();
    if (!accessToken) {
      console.error('Cannot connect WebSocket - no authentication token');
      setConnectionStatus('disconnected');
      connectingRef.current = false;
      return;
    }

    // Clear any pending timeouts to prevent conflicts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    if (networkRecoveryTimeoutRef.current) {
      clearTimeout(networkRecoveryTimeoutRef.current);
      networkRecoveryTimeoutRef.current = null;
    }
    if (manualReconnectTimeoutRef.current) {
      clearTimeout(manualReconnectTimeoutRef.current);
      manualReconnectTimeoutRef.current = null;
    }

    setConnectionStatus('connecting');
    
    // Include JWT token as query parameter for WebSocket authentication
    // Ensure sticky cookie (user_id) exists before opening WS by
    // touching a lightweight endpoint that also sets the cookie.
    try {
      const uid = getStableUid();
      await fetch(`${config.getApiUrl('/auth/me')}?uid=${encodeURIComponent(uid)}`, {
        headers: getAuthHeaders(accessToken),
        credentials: 'include'
      });
    } catch (e) {
      // Non-fatal; WS will still attempt to connect
    }
    const wsUrl = config.getWebSocketChatUrl(accessToken, getStableUid(), impersonatedUserId || undefined);
    const ws = new WebSocket(wsUrl);
    activeSocketTokenRef.current = accessToken;

    // CRITICAL FIX: Properly track connection timeout with auto-recovery
    connectionTimeoutRef.current = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        console.error('WebSocket connection timeout');
        ws.close();
        setConnectionStatus('disconnected');
        
        connectingRef.current = false; // RACE CONDITION FIX: Reset guard on timeout
        // CRITICAL FIX: Trigger reconnection logic instead of giving up
        if (shouldReconnectRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          addSystemNotification('Connection Timeout', `Retrying connection (${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})...`, 'warning');
          scheduleReconnect();
        } else {
          addSystemNotification('Connection Failed', 'Unable to connect. Please refresh the page.', 'error');
        }
      }
    }, 60000); // 60 second timeout to accommodate route creation
    
    ws.onopen = () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      connectingRef.current = false; // RACE CONDITION FIX: Reset guard on success
      setConnectionStatus('connected');
      console.log('WebSocket connected with authentication');
      
      activeSocketTokenRef.current = accessToken;

      // PRODUCTION ROBUSTNESS: Track connection time
      lastConnectedTimeRef.current = Date.now();
      
      // Show reconnection success notification if this was a reconnect
      const wasReconnecting = reconnectAttemptsRef.current > 0;
      
      // Reset reconnect attempts on successful connection
      reconnectAttemptsRef.current = 0;
      shouldReconnectRef.current = true;
      
      if (wasReconnecting) {
        addSystemNotification('Reconnected', 'Successfully reconnected to chat', 'success');
        
        // CRITICAL FIX: Always restore conversation context after reconnection
        // This handles both cases: existing session and mid-conversation reconnection
        if (currentChatSession) {
          console.log(`Restoring conversation context for session: ${currentChatSession}`);
          ws.send(JSON.stringify({
            type: 'restore_session',
            session_id: currentChatSession
          }));
        } else if (conversationId && messages.length > 0) {
          // Fallback: if we have a conversationId but no currentChatSession,
          // extract session_id from conversationId (format: user_id_timestamp)
          console.log(`Restoring conversation context for conversationId: ${conversationId}`);
          const sessionId = conversationId.includes('_') ? conversationId.split('_').slice(1).join('_') : conversationId;
          ws.send(JSON.stringify({
            type: 'restore_session',
            session_id: sessionId
          }));
        }
      }
      
      // Add welcome message only for brand new sessions
      // Use a ref to ensure this only happens once per app load
      if (!welcomeMessageAddedRef.current && messages.length === 0 && !currentChatSession) {
        welcomeMessageAddedRef.current = true;
        setTimeout(() => {
          addMessage('What is the action you want to integrate?', 'assistant', 'incoming');
        }, 500);
      }
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };
    
    ws.onclose = (event) => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      connectingRef.current = false; // RACE CONDITION FIX: Reset guard on close
      setConnectionStatus('disconnected');
      console.log('WebSocket disconnected with code:', event.code, 'reason:', event.reason);
      setWsConnection(null);
      activeSocketTokenRef.current = null;
      
      // If closed due to authentication failure (code 1008), try session recovery first
      if (event.code === 1008) {
        console.log('Authentication failed - attempting session recovery instead of immediate logout');

        // Use async IIFE to handle await in onClose handler
        (async () => {
          try {
            // Add small random delay to prevent all tabs from recovering simultaneously
            const delay = Math.random() * 1000; // 0-1 second random delay
            await new Promise(resolve => setTimeout(resolve, delay));

            // Try to recover the session before forcing logout
            const recoverySuccess = await attemptSessionRecovery();

            if (recoverySuccess) {
              console.log('✅ Session recovery successful - reconnecting WebSocket');

              // Only show notification if we're the active tab to avoid spam
              if (!document.hidden) {
                addSystemNotification('Session Recovered', 'Successfully refreshed authentication', 'success');
              }

              // Allow reconnection with fresh token
              shouldReconnectRef.current = true;
              scheduleReconnect();
              return;
            } else {
              console.log('❌ Session recovery failed - forcing sign out as fallback');
              shouldReconnectRef.current = false; // Disable auto-reconnect
              addSystemNotification('Authentication Failed', 'Please refresh the page to log in again', 'error');

              // Force sign out only if recovery failed
              handleSignOut().catch(error => {
                console.error('Error during forced sign out:', error);
              });
              return;
            }
          } catch (error) {
            console.error('❌ Error during session recovery:', error);
            shouldReconnectRef.current = false;
            addSystemNotification('Authentication Failed', 'Please refresh the page to log in again', 'error');
            handleSignOut().catch(err => {
              console.error('Error during forced sign out:', err);
            });
          }
        })();
        return;
      }
      
      // For all other disconnect reasons, attempt to reconnect
      if (shouldReconnectRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
        console.log(`Connection lost (code: ${event.code}), attempting to reconnect...`);
        addSystemNotification('Connection Lost', `Attempting to reconnect (${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})...`, 'warning');
        scheduleReconnect();
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.log('Max reconnection attempts reached');
        addSystemNotification('Connection Failed', 'Unable to reconnect. Please refresh the page.', 'error');
      }
    };
    
    ws.onerror = (error) => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      connectingRef.current = false; // RACE CONDITION FIX: Reset guard on error
      console.error('WebSocket error:', error);
      setConnectionStatus('disconnected');
      
      // Don't schedule reconnect here - let onclose handle it
      // This prevents double reconnection attempts
    };
    
    setWsConnection(ws);
  }, [attemptSessionRecovery, conversationId, currentChatSession, fetchFreshAccessToken, impersonatedUserId, messages.length, wsConnection]);

  useEffect(() => {
    const previous = previousImpersonatedUserIdRef.current;
    const current = impersonatedUserId || null;
    if (previous === current) {
      return;
    }
    previousImpersonatedUserIdRef.current = current;

    if (wsConnection) {
      try {
        wsConnection.close(4001, 'Impersonation target changed');
      } catch (error) {
        console.error('Error closing WebSocket on impersonation change:', error);
      }
    } else if (session?.access_token) {
      connectWebSocket();
    }
  }, [impersonatedUserId, wsConnection, session?.access_token, connectWebSocket]);

  useEffect(() => {
    if (!session?.access_token) {
      return;
    }

    if (connectionStatus === 'connected' || connectionStatus === 'connecting') {
      return;
    }

    if (connectingRef.current) {
      return;
    }

    connectWebSocket();
  }, [session?.access_token, connectionStatus, connectWebSocket, impersonatedUserId]);

  // Manual reconnect function
  const manualReconnect = () => {
    console.log('Manual reconnect triggered');
    
    // Reset attempts and enable reconnection
    reconnectAttemptsRef.current = 0;
    shouldReconnectRef.current = true;
    
    // Clear any pending timeouts - CRITICAL FIX: Include connection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    // CRITICAL FIX: Properly close existing connection and clean state
    if (wsConnection) {
      // Set status immediately to prevent race conditions
      setConnectionStatus('disconnected');
      setWsConnection(null);
      wsConnection.close();
    }
    
    // Reset connection guard for manual reconnect
    connectingRef.current = false;
    
    // Immediate reconnect with proper delay
    manualReconnectTimeoutRef.current = setTimeout(connectWebSocket, 100);
    
    addSystemNotification('Reconnecting', 'Attempting to reconnect...', 'info');
  };

  const playResponseBeep = useCallback(() => {
    if (!responseBeepRef.current) {
      return;
    }

    responseBeepRef.current.currentTime = 0;
    responseBeepRef.current.play().catch(() => {
      // Ignore autoplay restrictions; user interaction may be required once
    });
  }, []);

  const handleWebSocketMessage = (data: any) => {
    console.log('🎯 Received WebSocket message:', data);
    
    switch (data.type) {
      case 'tool_call':
        console.log('📞 Processing tool_call');
        // Add a message showing the tool call with enhanced formatting
        const truncatedArguments = (() => {
          try {
            return smartTruncate(data.arguments, { maxLength: 300, preserveStructure: true });
          } catch (error) {
            console.error('Error truncating tool arguments:', error);
            return JSON.stringify(data.arguments, null, 2);
          }
        })();
        const toolCallMessage = `🔧 **Tool Call**: \`${data.function_name}\`\n\n**Arguments:**\n\`\`\`json\n${truncatedArguments}\n\`\`\``;
        addMessageWithType(toolCallMessage, 'system', 'incoming', 'tool_call');
        
        // Also update the function calls state for the dedicated section
        setFunctionCalls(prev => [...prev, {
          function_name: data.function_name,
          arguments: data.arguments,
          call_id: data.call_id
        }]);
        break;
        
      case 'tool_response':
        console.log('📋 Processing tool_response');
        // Add a message showing the tool response with enhanced formatting
        const responsePreview = typeof data.response === 'string' ? data.response : JSON.stringify(data.response, null, 2);
        const truncatedResponse = (() => {
          try {
            return truncateToolResponse(data.response, data.function_name);
          } catch (error) {
            console.error('Error truncating tool response:', error);
            return smartTruncate(responsePreview, { maxLength: 500, preserveStructure: true });
          }
        })();
        
        let toolResponseMessage = `✅ **Tool Response**: \`${data.function_name}\`\n\n`;
        
        // Format the response based on its type
        if (typeof data.response === 'object' && data.response !== null) {
          toolResponseMessage += `**Result:**\n\`\`\`json\n${truncatedResponse}\n\`\`\``;
        } else if (typeof data.response === 'string' && data.response.trim().startsWith('{')) {
          // Looks like JSON string
          toolResponseMessage += `**Result:**\n\`\`\`json\n${truncatedResponse}\n\`\`\``;
        } else {
          // Plain text response
          toolResponseMessage += `**Result:**\n\`\`\`\n${truncatedResponse}\n\`\`\``;
        }
        
        addMessageWithType(toolResponseMessage, 'system', 'incoming', 'tool_response');
        
        // Update the function calls state with the result
        setFunctionCalls(prev => prev.map(call => 
          call.call_id === data.call_id || call.function_name === data.function_name
            ? { ...call, result: typeof data.response === 'string' ? data.response : JSON.stringify(data.response) }
            : call
        ));
        break;
        
      case 'agent_text':
        console.log('💬 Processing agent_text:', data.content);
        console.log('📝 Current response before:', currentResponseRef.current);
        // Handle regular text content from the agent
        // Note: Don't set isTyping here as it should already be true from loading_start
        currentResponseRef.current += data.content;
        console.log('📝 Current response after:', currentResponseRef.current);
        updateCurrentResponse();
        break;
        
      case 'final_response':
        console.log('🏁 Processing final_response:', data.content);
        console.log('🔍 [EMPTY_BUBBLE_DEBUG] final_response received:', {
          hasContent: !!data.content,
          contentLength: data.content ? data.content.length : 0,
          contentType: typeof data.content,
          dataKeys: Object.keys(data),
          userId: data.user_id
        });
        
        // Handle the final response
        setIsTyping(false);
        setLoadingMessage('');
        if (data.content) {
          currentResponseRef.current = data.content;
          console.log('📝 Final response set to:', currentResponseRef.current);
          updateCurrentResponse();
        } else {
          console.error('🚨 [EMPTY_BUBBLE_DEBUG] EMPTY final_response received!', {
            data,
            currentResponseRef: currentResponseRef.current,
            timestamp: new Date().toISOString()
          });
        }

        const resolvedContent = (currentResponseRef.current || '').trim();
        if (resolvedContent) {
          playResponseBeep();
        }
        finalizeResponse();
        // Clear the ref AFTER everything is done
        setTimeout(() => {
          currentResponseRef.current = '';
          console.log('🧹 Cleared currentResponseRef after finalizing');
        }, 100);
        break;
        
      case 'text_chunk':
        console.log('📄 Processing text_chunk (legacy):', data.content);
        // Legacy support for old message type
        // Note: Don't set isTyping here as it should already be true from loading_start
        currentResponseRef.current += data.content;
        updateCurrentResponse();
        break;
        
      case 'function_call':
        console.log('⚙️ Processing function_call (legacy)');
        // Legacy support
        setFunctionCalls(prev => [...prev, {
          function_name: data.function_name,
          arguments: data.arguments
        }]);
        break;
        
      case 'function_result':
        console.log('✅ Processing function_result (legacy)');
        // Legacy support
        setFunctionCalls(prev => prev.map(call => 
          call.function_name === data.function_name 
            ? { ...call, result: data.result }
            : call
        ));
        break;
        
      case 'complete':
        console.log('✅ Processing complete');
        setIsTyping(false);
        setLoadingMessage('');
        finalizeResponse();
        
        // Refresh chat sessions to pick up any new previews (especially for first messages)
        loadChatSessions();
        break;
        
      case 'error':
        console.log('❌ Processing error:', data.content);
        setIsTyping(false);
        setLoadingMessage('');
        addSystemNotification(`Error: ${data.content}`, 'error');
        break;
        
      case 'session_info':
        console.log('ℹ️ Session info received:', data.session_id);
        console.log('Current messages length:', messages.length);
        console.log('Current chat session:', currentChatSession);
        setConversationId(data.session_id);
        
        // Don't add welcome message here - it might be called multiple times
        // Instead, we'll add it only once when the component first mounts
        break;
        
      case 'session_restored':
        console.log('✅ Session restored:', data.session_id);
        addSystemNotification(`AI conversation context restored for session ${data.session_id}`, 'success');
        break;
        
      case 'loading_start':
        console.log('⏳ Loading started:', data.message);
        console.log('Setting isTyping to true');
        setIsTyping(true);
        setLoadingMessage(data.message || 'Processing...');
        break;
        
      case 'loading_update':
        console.log('⏳ Loading update:', data.message);
        console.log('Current isTyping state:', isTyping);
        setLoadingMessage(data.message || 'Processing...');
        break;
        
      case 'route_popup':
        console.log('Route created - showing popup:', data.route_info?.name);
                setRoutePopupData({
          route_info: data.route_info,
          curl_examples: data.curl_examples
        });
        setShowRoutePopup(true);

        break;
        
      case 'new_tab_detected':
        console.log('🆕 New tab detected:', data.session_id, data.tab_count, 'tabs');
        
        // If this is for the current live session, automatically switch to the newest tab
        if (liveViewSession && data.session_id === liveViewSession.session_id && data.tabs.length > 0) {
          const newestTab = data.tabs[data.tabs.length - 1];
          setLiveViewSession(prev => prev ? {...prev, debug_url: newestTab.debugger_fullscreen_url} : null);
          setCurrentTabIndex(newestTab.tab_index);
          console.log('🔄 Auto-switched to new tab:', newestTab.tab_index, newestTab.url);
        }
        
        // Dispatch custom event for TabManager to handle
        const newTabEvent = new CustomEvent('new-tab-detected', {
          detail: {
            session_id: data.session_id,
            tab_count: data.tab_count,
            tabs: data.tabs
          }
        });
        window.dispatchEvent(newTabEvent);
        
        // Show notification message in chat
        addMessage(`🆕 New browser tab opened! Automatically switched to the new tab (${data.tab_count} tabs total).`, 'system', 'incoming');
        break;
        
      case 'download_detected':
        console.log('📥 Download detected:', data.filename);
        
        // Show a notification message in the chat
        addMessage(`📥 Download started: ${data.filename}`, 'system', 'incoming');
        
        // You could also show a toast notification or other UI element here
        // For now, we'll just log it to the chat
        
        break;
        
      default:
        console.log('❓ Unknown message type:', data.type, data);
        break;
    }
  };

  const updateCurrentResponse = () => {
    console.log('🔄 updateCurrentResponse called with:', currentResponseRef.current);
    
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      
      console.log('👆 Last message:', lastMessage);
      
      if (lastMessage && lastMessage.sender === 'assistant') {
        console.log('📝 Updating existing assistant message');
        lastMessage.content = currentResponseRef.current;
      } else {
        console.log('➕ Creating new assistant message');
        newMessages.push({
          id: `msg-${messageIdRef.current++}`,
          content: currentResponseRef.current,
          sender: 'assistant',
          direction: 'incoming',
          timestamp: new Date()
        });
      }
      
      console.log('📋 Total messages after update:', newMessages.length);
      return newMessages;
    });
    
    // Trigger scroll when response is updated
    scrollToBottom();
  };

  const finalizeResponse = () => {
    const responseContent = currentResponseRef.current;
    console.log('🏁 finalizeResponse called with:', responseContent);
    console.log('🔍 [EMPTY_BUBBLE_DEBUG] finalizeResponse details:', {
      hasContent: !!responseContent,
      contentLength: responseContent ? responseContent.length : 0,
      contentType: typeof responseContent,
      isEmpty: responseContent === '',
      isWhitespace: responseContent ? responseContent.trim() === '' : false
    });
    
    // Extract and parse code blocks from the response
    const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
    const response = currentResponseRef.current;
    const foundCodeBlocks: CodeBlock[] = [];
    let match;

    while ((match = codeBlockRegex.exec(response)) !== null) {
      const language = match[1] || 'text';
      const code = match[2].trim();
      
      if (code) {
        const codeBlock: CodeBlock = {
          id: `code-${Date.now()}-${foundCodeBlocks.length}`,
          language,
          code,
          title: `${language} code`
        };
        foundCodeBlocks.push(codeBlock);
      }
    }

    if (foundCodeBlocks.length > 0) {
      setCodeBlocks(prev => [...prev, ...foundCodeBlocks]);
      if (!activeCodeTab) {
        setActiveCodeTab(foundCodeBlocks[0].id);
      }
    }

    console.log('✅ finalizeResponse completed, keeping response intact');
  };

  // Code execution functions
  const executeJavaScript = (code: string): Promise<{ output: string; error?: string }> => {
    return new Promise((resolve) => {
      try {
        // Create a custom console to capture output
        const logs: string[] = [];
        const originalConsole = {
          log: console.log,
          error: console.error,
          warn: console.warn,
          info: console.info
        };

        // Override console methods to capture output
        const mockConsole = {
          log: (...args: any[]) => {
            logs.push(args.map(arg => 
              typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
            ).join(' '));
            originalConsole.log(...args); // Still log to real console
          },
          error: (...args: any[]) => {
            logs.push('ERROR: ' + args.map(arg => String(arg)).join(' '));
            originalConsole.error(...args);
          },
          warn: (...args: any[]) => {
            logs.push('WARN: ' + args.map(arg => String(arg)).join(' '));
            originalConsole.warn(...args);
          },
          info: (...args: any[]) => {
            logs.push('INFO: ' + args.map(arg => String(arg)).join(' '));
            originalConsole.info(...args);
          }
        };

        // Create a safe execution environment
        const executeCode = new Function('console', `
          try {
            ${code}
          } catch (error) {
            console.error(error.message);
            throw error;
          }
        `);

        // Execute the code
        const result = executeCode(mockConsole);
        
        // If there's a return value, add it to logs
        if (result !== undefined) {
          logs.push('Return value: ' + (typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result)));
        }

        resolve({
          output: logs.length > 0 ? logs.join('\n') : 'Code executed successfully (no output)'
        });
      } catch (error) {
        resolve({
          output: '',
          error: error instanceof Error ? error.message : String(error)
        });
      }
    });
  };

  const executePython = async (code: string): Promise<{ output: string; error?: string }> => {
    try {
      // Send Python code to backend for execution
      const response = await fetch(config.getApiUrl('execute-python'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ code })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        output: '',
        error: `Failed to execute Python code: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  };

  const executeCode = async (codeBlockId: string) => {
    const codeBlock = codeBlocks.find(block => block.id === codeBlockId);
    if (!codeBlock) return;

    // Set executing state
    setCodeBlocks(prev => prev.map(block => 
      block.id === codeBlockId 
        ? { ...block, isExecuting: true }
        : block
    ));

    let result: { output: string; error?: string };

    try {
      switch (codeBlock.language.toLowerCase()) {
        case 'javascript':
        case 'js':
          result = await executeJavaScript(codeBlock.code);
          break;
        case 'python':
        case 'py':
          result = await executePython(codeBlock.code);
          break;
        default:
          result = {
            output: '',
            error: `Execution not supported for ${codeBlock.language}. Supported languages: JavaScript, Python`
          };
      }
    } catch (error) {
      result = {
        output: '',
        error: error instanceof Error ? error.message : String(error)
      };
    }

    // Update execution results with truncated output
    const truncatedResult = {
      ...result,
      output: result.output ? smartTruncate(result.output, { maxLength: 1000, preserveStructure: false }) : result.output,
      error: result.error ? smartTruncate(result.error, { maxLength: 500, preserveStructure: false }) : result.error,
      timestamp: new Date()
    };
    
    setExecutionResults(prev => ({
      ...prev,
      [codeBlockId]: truncatedResult
    }));

    // Clear executing state
    setCodeBlocks(prev => prev.map(block => 
      block.id === codeBlockId 
        ? { ...block, isExecuting: false }
        : block
    ));
  };

  const clearExecutionResult = (codeBlockId: string) => {
    setExecutionResults(prev => {
      const newResults = { ...prev };
      delete newResults[codeBlockId];
      return newResults;
    });
  };

  const addMessage = (content: string, sender: string, direction: 'incoming' | 'outgoing') => {
    addMessageWithType(content, sender, direction, 'normal');
  };

  // System notification function with active tab isolation
  const addSystemNotification = (title: string, message: string = '', type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    // Only show notifications in the active tab to prevent multi-tab spam
    if (!isActiveTab) {
      console.log(`[Tab Inactive] Suppressed notification: ${title} - ${message}`);
      return;
    }
    addNotification(title, message, type);
  };

  const addMessageWithType = (content: string, sender: string, direction: 'incoming' | 'outgoing', messageType: 'tool_call' | 'tool_response' | 'normal' = 'normal') => {
    const newMessage: ChatMessage = {
      id: `msg-${messageIdRef.current++}`,
      content,
      sender,
      direction,
      timestamp: new Date(),
      messageType
    };
    
    console.log('Adding message:', newMessage);
    setMessages(prev => {
      const updated = [...prev, newMessage];
      console.log('Total messages:', updated.length);
      return updated;
    });
    
    // Trigger scroll after message is added
    setTimeout(() => scrollToBottom(), 100);
  };

  // Function to strip HTML and convert to plain text
  const stripHtmlAndConvertToPlainText = (html: string): string => {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Extract plain text, preserving line breaks
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up only excessive whitespace, preserve single spaces
    return plainText
      .replace(/[ \t]{2,}/g, ' ') // Replace 2+ consecutive spaces/tabs with single space
      .replace(/\n\s+/g, '\n') // Clean up line breaks with spaces
      .replace(/^\s+|\s+$/g, ''); // Trim start and end only
  };

  const handleSendMessage = (message: string) => {
    if (!message.trim() || !wsConnection || connectionStatus !== 'connected') {
      return;
    }

    // Strip HTML formatting completely for both display and LLM
    const plainTextMessage = stripHtmlAndConvertToPlainText(message);
    
    console.log('Original message:', message);
    console.log('Plain text message (display and LLM):', plainTextMessage);
    
    // Add plain text message to chat (no HTML formatting in display)
    addMessage(plainTextMessage, 'user', 'outgoing');
    
    // Clear current response
    currentResponseRef.current = '';
    
    // Set loading state immediately
    setIsTyping(true);
    setLoadingMessage('Processing...');
    
    // Send plain text message to backend with JS call frames setting
    wsConnection.send(JSON.stringify({
      type: 'user_message',
      content: plainTextMessage,
      include_js_call_frames: includeJsCallFrames
    }));
    
    setCurrentMessage('');
  };

  const getLanguageFromExtension = (language: string): string => {
    const languageMap: Record<string, string> = {
      js: 'javascript',
      jsx: 'javascript',
      ts: 'typescript',
      tsx: 'typescript',
      py: 'python',
      html: 'html',
      css: 'css',
      json: 'json',
      md: 'markdown',
      sql: 'sql',
      sh: 'shell',
      bash: 'shell',
      yaml: 'yaml',
      yml: 'yaml',
      xml: 'xml'
    };
    
    return languageMap[language.toLowerCase()] || language;
  };

  // Live View functions
  const createLiveViewSession = async () => {
    if (!session?.access_token) {
      addSystemNotification('Authentication Required', 'Please log in to continue', 'error');
      return;
    }

    if (!conversationId) {
      addSystemNotification('No Conversation ID', 'Please refresh the page', 'error');
      return;
    }
    
    setIsCreatingSession(true);
    try {
      const response = await fetch(config.getApiUrl('/browser/session'), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          conversation_id: conversationId
        })
      });

      if (response.ok) {
        const result = await response.json();
                setLiveViewSession({
          session_id: result.session_id,
          debug_url: result.debug_url,
          status: 'active',
          har_recording: false
        });
        
        // Clear existing context flag for new sessions
        setIsExistingContext(null);
        
        addSystemNotification('Live View Session Created', `Session ID: ${result.session_id}`, 'success');
      } else if (response.status === 401) {
        addSystemNotification('Authentication Failed', 'Please log in again to continue', 'error');
      } else {
        const errorText = await response.text();
        addSystemNotification('Session Creation Failed', `Failed to create session: ${errorText}`, 'error');
      }
    } catch (error) {
      console.error('Error creating session:', error);
      addSystemNotification('Session Error', `Error creating session: ${error}`, 'error');
    } finally {
      setIsCreatingSession(false);
    }
  };

  const startHarRecording = async () => {
    if (!session?.access_token) {
      addSystemNotification('Authentication Required', 'Please log in to continue', 'error');
      return;
    }

    if (!liveViewSession) {
      addSystemNotification('No active session. Create a session first.', 'warning');
      return;
    }
    
    setHarRecordingStatus('starting');
    try {
      const response = await fetch(config.getApiUrl('/browser/har-recording'), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          session_id: liveViewSession.session_id,
          action: 'start'
        })
      });

      if (response.ok) {
        const result = await response.json();
        setHarRecordingStatus('recording');
        setLiveViewSession(prev => prev ? {...prev, har_recording: true} : null);
        
        // Refresh the live view iframe with a proper reload
        const iframe = document.querySelector('iframe[title="Live Browser View"]') as HTMLIFrameElement;
        if (iframe) {
          // Method 1: Add timestamp to force reload
          const currentSrc = iframe.src;
          const separator = currentSrc.includes('?') ? '&' : '?';
          iframe.src = `${currentSrc}${separator}_refresh=${Date.now()}`;
          
          // Method 2: Use contentWindow reload as backup
          setTimeout(() => {
            try {
              iframe.contentWindow?.location.reload();
            } catch (e) {
              // Cross-origin restriction, the timestamp method should work
            }
          }, 100);
        }
        
        addSystemNotification(`Action recording started!`, 'success');
      } else if (response.status === 401) {
        addSystemNotification('Authentication failed. Please log in again.', 'error');
        setHarRecordingStatus('idle');
      } else {
        const errorText = await response.text();
        addSystemNotification(`Failed to start action recording: ${errorText}`, 'error');
        setHarRecordingStatus('idle');
      }
    } catch (error) {
      console.error('Error starting action recording:', error);
      addSystemNotification(`Error starting action recording: ${error}`, 'error');
      setHarRecordingStatus('idle');
    }
  };

  const stopHarRecording = async () => {
    if (!session?.access_token) {
      addSystemNotification('Authentication Required', 'Please log in to continue', 'error');
      return;
    }

    if (!liveViewSession) {
      addSystemNotification('No active session found.', 'warning');
      return;
    }
    
    setHarRecordingStatus('stopping');
    try {
      const response = await fetch(config.getApiUrl('/browser/har-recording'), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          session_id: liveViewSession.session_id,
          action: 'stop'
        })
      });

      if (response.ok) {
        const result = await response.json();
        setHarRecordingStatus('idle');
        setLiveViewSession(prev => prev ? {...prev, har_recording: false} : null);
        
        if (result.har_file) {
          // Store the created action recording name and ID for potential deletion
          setLastCreatedHarFile(result.har_file);
          setLastCreatedHarFileId(result.file_id); // Store the file_id for easy deletion

          console.log('📁 action recording created:', {
            filename: result.har_file,
            file_id: result.file_id,
            file_size: result.file_size,
            entries_count: result.entries_count
          });

          addSystemNotification(`Action recording stopped - review and save or cancel`, 'info');

          // Show naming dialog
          setShowNamingDialog(true);
          setNewHarFileName(result.har_file.replace('.har', ''));
        } else {
          addSystemNotification(`Action recording stopped: ${result.message || 'Recording complete'}`, 'success');
        }
        
        // Refresh the action recordings list 
        await loadHarFiles();
      } else if (response.status === 401) {
        addSystemNotification('Authentication failed. Please log in again.', 'error');
        setHarRecordingStatus('idle');
      } else {
        const errorText = await response.text();
        addSystemNotification(`Failed to stop recording: ${errorText}`, 'error');
        setHarRecordingStatus('idle');
      }
    } catch (error) {
      console.error('Error stopping action recording:', error);
      addSystemNotification(`Error stopping action recording: ${error}`, 'error');
      setHarRecordingStatus('idle');
    }
  };

  const terminateLiveViewSession = async () => {
    if (!session?.access_token) {
      addSystemNotification('Authentication Required', 'Please log in to continue', 'error');
      return;
    }

    if (!liveViewSession) {
      addSystemNotification('No active session to terminate.', 'warning');
      return;
    }

    if (isTerminatingSession) {
      // Prevent multiple clicks
      return;
    }
    
    setIsTerminatingSession(true);
    try {
      const response = await fetch(config.getApiUrl(`/browser/session/${liveViewSession.session_id}/terminate`), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🧠 Terminate session response:', result);
        
        // Check if there's a context ID to save
        const contextId = result.context_id;
        const currentUrl = result.current_url;
        console.log('🧠 Context ID from response:', contextId);
        console.log('🌐 Current URL from response:', currentUrl);
        
        if (contextId) {
          // Check if this was an existing saved context
          if (isExistingContext && isExistingContext.contextId === contextId) {
            // Update existing context with new URL
            console.log('🔄 Updating existing context with new URL');
            await updateExistingContext(contextId, currentUrl, isExistingContext.displayName);
          } else {
            // Show context naming dialog for new context
            setContextToSave({
              sessionId: liveViewSession.session_id,
              contextId: contextId,
              currentUrl: currentUrl
            });
            setShowContextNamingDialog(true);
            console.log('🧠 Context naming dialog should be shown');
          }
        } else {
          console.log('🧠 No context ID found in response');
        }
        
        // Reset session state
        setLiveViewSession(null);
        setHarRecordingStatus('idle');
        setIsExistingContext(null);
        
        addSystemNotification(`Session terminated successfully!`, 'success');
      } else if (response.status === 401) {
        addSystemNotification('Authentication failed. Please log in again.', 'error');
      } else {
        const errorText = await response.text();
        addSystemNotification(`Failed to terminate session: ${errorText}`, 'error');
      }
    } catch (error) {
      console.error('Error terminating session:', error);
      addSystemNotification(`Error terminating session: ${error}`, 'error');
    } finally {
      setIsTerminatingSession(false);
    }
  };

  const activeCodeBlock = codeBlocks.find(block => block.id === activeCodeTab);
  const activeExecutionResult = activeCodeTab ? executionResults[activeCodeTab] : null;

  // Resizable drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartChatWidth(chatWidth);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    // Throttle resize updates to prevent ResizeObserver loops
    window.requestAnimationFrame(() => {
      const containerWidth = window.innerWidth;
      const deltaX = e.clientX - dragStartX;
      const deltaPercent = (deltaX / containerWidth) * 100;

      const newChatWidth = Math.max(25, Math.min(75, dragStartChatWidth + deltaPercent));
      setChatWidth(newChatWidth);
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, dragStartX, dragStartChatWidth, chatWidth]);

  // If this is the routes page, render the routes component
  if (isRoutesPage) {
    return (
      <div className="h-screen w-screen bg-zinc-950 flex flex-col">
        {/* Header */}
        <div className="bg-zinc-900 px-5 py-4 border-b border-zinc-800 flex justify-between items-center w-full">
          <div className="flex items-center gap-4">
            <h1 className="text-zinc-200 font-semibold text-lg">Routes</h1>
            <span className="text-zinc-400 text-sm">Network Traffic Analysis</span>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Back to Main App Button */}
            <button
              onClick={() => window.open('/', '_blank')}
              className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            >
              ← Back to Chat
            </button>
            
            {/* User Info */}
            {user && (
              <div className="flex items-center gap-2">
                {user.user_metadata?.avatar_url ? (
                  <img
                    src={user.user_metadata.avatar_url}
                    alt="User avatar"
                    className="w-6 h-6 rounded-full object-cover border border-zinc-600"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                    {(user.user_metadata?.full_name || user.email || '').charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-zinc-300 text-sm">{user.user_metadata?.full_name || 'User'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Routes Content */}
        <div className="flex-1 w-full overflow-hidden">
          <RoutesDisplay />
        </div>

        {/* Tutorial overlay intentionally not shown on routes page (button lives in main chat header) */}
      </div>
    );
  }

  if (isLoginAutomationPage) {
    return (
      <div className="h-screen w-screen bg-zinc-950 flex flex-col">
        <LoginAutomation />
      </div>
    );
  }

  // If this is the cookies page, render the cookies dashboard
  if (isCookiesPage) {
    return (
      <div className="h-screen w-screen bg-zinc-950 flex flex-col">
        {/* Header */}
        <div className="bg-zinc-900 px-5 py-4 border-b border-zinc-800 flex justify-between items-center w-full">
          <div className="flex items-center gap-4">
            <h1 className="text-zinc-200 font-semibold text-lg">🍪 Cookies Dashboard</h1>
            <span className="text-zinc-400 text-sm">Authentication Data Extraction</span>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Back to Main App Button */}
            <button
              onClick={() => window.open('/', '_blank')}
              className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            >
              ← Back to Chat
            </button>
            
            {/* User Info */}
            {user && (
              <div className="flex items-center gap-2">
                {user.user_metadata?.avatar_url ? (
                  <img
                    src={user.user_metadata.avatar_url}
                    alt="User avatar"
                    className="w-6 h-6 rounded-full object-cover border border-zinc-600"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                    {(user.user_metadata?.full_name || user.email || '').charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-zinc-300 text-sm">{user.user_metadata?.full_name || 'User'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Cookies Dashboard Content */}
        <div className="flex-1 w-full overflow-hidden">
          <CookiesDashboard />
        </div>

        {/* Global Tutorial button and overlay for cookies page */}
        <button
          onClick={() => setShowArcadeOverlay(true)}
          className="fixed bottom-6 right-6 z-[9980] bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg shadow-violet-700/30 transition-all"
          title="Open Interactive Tutorial"
        >
          ❓ Tutorial
        </button>
        {showArcadeOverlay && (
          <ArcadeOverlay
            onClose={() => setShowArcadeOverlay(false)}
            embedUrl="https://demo.arcade.software/R5Fr3xvJJ9AHwt7yxvnA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true"
            title="Create a One‑on‑One Meeting in Calendly and Generate an API Request"
          />
        )}
      </div>
    );
  }

  // If this is the usage page, render the usage dashboard
  if (isUsagePage) {
    return (
      <UsageDashboard />
    );
  }

  // If this is the admin page, render the admin panel
  if (isAdminPage) {
    return (
      <div className="h-screen w-screen bg-zinc-950 overflow-y-auto">
        <AdminPanel />
      </div>
    );
  }

  // If this is the tests page, render the tests dashboard
  if (isTestsPage) {
    // Admin check - specific email addresses
    const adminEmails = ['neeliyer14@gmail.com', 'richard@taiki.online'];
    const isAdmin = user?.email && adminEmails.includes(user.email);
    
    if (!isAdmin) {
      return (
        <div className="h-screen w-screen bg-zinc-950 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-400 mb-2">Access Denied</h1>
            <p className="text-zinc-400">You don't have permission to access this page.</p>
            <button
              onClick={() => window.open('/', '_blank')}
              className="mt-4 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-200 transition-colors"
            >
              ← Back to Chat
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="h-screen w-screen bg-zinc-950 flex flex-col">
        {/* Header */}
        <div className="bg-zinc-900 px-5 py-4 border-b border-zinc-800 flex justify-between items-center w-full">
          <div className="flex items-center gap-4">
            <h1 className="text-zinc-200 font-semibold text-lg">🧪 Tests Dashboard</h1>
            <span className="text-zinc-400 text-sm">Admin Panel</span>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Back to Main App Button */}
            <button
              onClick={() => window.open('/', '_blank')}
              className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            >
              ← Back to Chat
            </button>
            
            {/* User Info */}
            {user && (
              <div className="flex items-center gap-2">
                {user.user_metadata?.avatar_url ? (
                  <img
                    src={user.user_metadata.avatar_url}
                    alt="User avatar"
                    className="w-6 h-6 rounded-full object-cover border border-zinc-600"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                    {(user.user_metadata?.full_name || user.email || '').charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-zinc-300 text-sm">{user.user_metadata?.full_name || 'User'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tests Content */}
        <div className="flex-1 w-full overflow-hidden">
          <TestsDashboard />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full">
      {/* Impersonation Banner - shows on all pages if impersonating */}
      <ImpersonationBanner />
      
      {/* Spacer for impersonation banner */}
      <div style={{ 
        height: localStorage.getItem('admin_impersonation_token') ? '88px' : '0px',
        flexShrink: 0
      }} />
      
      {/* Notification Toast Container */}
      <NotificationToast
        notifications={notifications}
        onRemove={removeNotification}
      />
      
      {/* Full Width Header */}
      <div className="bg-zinc-900 px-5 py-2 border-b border-zinc-800 flex justify-between items-center w-full">
        <span className="text-zinc-200 font-semibold text-sm uppercase tracking-wide">Integuru</span>
        
        <div className="flex items-center gap-4">
          {/* Routes Button */}
          <button
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            onClick={() => window.open('/?page=routes', '_blank')}
          >
            Routes
          </button>

          {/* Cookies Dashboard Button */}
          <button
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            onClick={() => window.open('/?page=cookies', '_blank')}
          >
            Cookies
          </button>

          {/* Login Automation Page Button */}
          <button
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            onClick={() => window.open('/?page=login-automation', '_blank')}
          >
            Login Automation
          </button>

          {/* Admin Button - Only show for admins */}
          {user?.email && ['neeliyer14@gmail.com', 'alanlu1999@gmail.com','richard@taiki.online'].includes(user.email) && (
            <button
              className="text-sm font-medium text-yellow-400 hover:text-yellow-200 transition-colors duration-200"
              onClick={() => window.open('/?page=admin', '_blank')}
              title="Admin Panel"
            >
              👑 Admin
            </button>
          )}

          {/* Tutorial Button */}
          <button
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            onClick={() => setShowArcadeOverlay(true)}
            title="Open Interactive Tutorial"
          >
            Tutorial
          </button>


          {/* Usage Dashboard hyperlink*/}
          <button
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            onClick={() => window.open('/?page=usage', '_blank')}
          >
            Usage
          </button>

          {/* Tests Dashboard Button - Only show for admins */}
          {user?.email && ['neeliyer14@gmail.com'].includes(user.email) && (
            <button
              className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
              onClick={() => window.open('/?page=tests', '_blank')}
            >
              Tests
            </button>
          )}

          {/* Connection Status */}
          <div 
            className={`
              w-2 h-2 rounded-full transition-all duration-300
              ${connectionStatus === 'connected' 
                ? 'bg-emerald-500 animate-pulse-slow shadow-[0_0_6px_rgb(16_185_129)]' 
                : connectionStatus === 'connecting'
                ? 'bg-yellow-500 animate-spin shadow-[0_0_6px_rgb(255_165_0)]'
                : 'bg-red-500 shadow-[0_0_6px_rgb(239_68_68)] cursor-pointer hover:bg-red-400'
              }
            `}
            title={
              connectionStatus === 'connected' 
                ? 'Connected' 
                : connectionStatus === 'connecting'
                ? 'Connecting...'
                : 'Disconnected - Click to reconnect'
            }
            onClick={() => {
              if (connectionStatus === 'disconnected') {
                manualReconnect();
              }
            }}
          ></div>
          
          {/* Secret Key Manager Button */}
          <button
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            onClick={() => setShowSecretKeyManager(!showSecretKeyManager)}
          >
            🔑 API Keys
          </button>

          {/* FAQ Button */}
          <button
            className="text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors duration-200"
            onClick={() => setShowFAQPopup(true)}
          >
            FAQ
          </button>
          

          
          {/* User Profile Dropdown */}
          <div className="relative" ref={userDropdownRef}>
            <button
              className="flex items-center gap-2 hover:bg-white/5 rounded-lg p-2 transition-colors duration-200"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
            >
              {user?.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt="User avatar"
                  className="w-8 h-8 rounded-full object-cover border-2 border-zinc-600"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                  {(user?.user_metadata?.full_name || user?.email || '').charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-zinc-200 text-sm font-medium">{user?.user_metadata?.full_name || 'User'}</span>
              <span className="text-zinc-400 text-xs">▼</span>
            </button>
            
            {showUserDropdown && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-zinc-900 border border-white/10 rounded-xl shadow-xl shadow-black/50 z-[9999]">
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-center gap-3 mb-3">
                    {user?.user_metadata?.avatar_url ? (
                      <img
                        src={user.user_metadata.avatar_url}
                        alt="User avatar"
                        className="w-12 h-12 rounded-full object-cover border-2 border-zinc-600"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-lg font-semibold">
                        {(user?.user_metadata?.full_name || user?.email || '').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-zinc-200 font-semibold text-sm">{user?.user_metadata?.full_name || 'User'}</div>
                      <div className="text-zinc-400 text-xs break-all">{user?.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setShowUserDropdown(false);
                    }}
                    className="w-full bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    🚪 Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="app-container flex-1">
        {/* Chat Section */}
        <div className="chat-section" style={{ flex: `0 0 ${chatWidth}%` }}>
          <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 px-3 py-3 border-b border-zinc-800 flex items-center backdrop-blur-sm relative min-h-[64px]" style={{ zIndex: 99999, gap: `${Math.max(6, chatWidth * 0.12)}px` }}>
          {/* Deep Analysis Toggle - Hidden */}
          {/* <div 
            className={`
              h-10 px-3 rounded-lg border transition-all duration-200 flex items-center justify-center flex-shrink-0
              ${includeJsCallFrames 
                ? 'bg-gradient-to-r from-amber-500 to-red-500 border-transparent shadow-lg shadow-amber-500/20' 
                : 'bg-white/5 border-white/10 hover:border-white/20'
              }
            `}
            style={{ 
              minWidth: '60px',
              width: 'auto'
            }}
          >
            <label className="flex items-center cursor-pointer text-xs font-medium text-white m-0 select-none w-full justify-center" style={{ gap: `${Math.max(3, chatWidth * 0.08)}px` }}>
              <input
                type="checkbox"
                checked={includeJsCallFrames}
                onChange={(e) => setIncludeJsCallFrames(e.target.checked)}
                className="sr-only"
              />
              <span className="flex-shrink-0 whitespace-nowrap">Deep Analysis</span>
              <span className={`
                text-xs px-1.5 py-0.5 rounded font-semibold flex-shrink-0
                ${includeJsCallFrames ? 'bg-white/20' : 'bg-white/10'}
              `}>
                {includeJsCallFrames ? "ON" : "OFF"}
              </span>
            </label>
          </div> */}

          {/* Action Recording Selector */}
          <div className="flex items-center relative" ref={harDropdownRef}>
            <button
              className="
                h-10 px-4 bg-white/5 border border-white/10 rounded-lg
                text-zinc-200 text-sm font-medium cursor-pointer
                transition-all duration-200 flex items-center justify-between
                hover:bg-white/10 hover:border-white/20
                focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50
                disabled:opacity-40 disabled:cursor-not-allowed
              "
              style={{
                minWidth: '200px',
                width: '250px',
                maxWidth: '350px'
              }}
              onClick={() => {
                if (!isSwitchingHarFile && !isLoadingAvailableFiles) {
                  setShowHarDropdown(!showHarDropdown);
                  if (!showHarDropdown) {
                    loadCurrentHarFileInfo();
                    loadAvailableHarFiles();
                  }
                }
              }}
              disabled={isSwitchingHarFile || isLoadingAvailableFiles}
              title={currentHarFile ? `Active: ${currentHarFile.filename}` : 'Select action recording file'}
            >
              <span className="truncate">
                {currentHarFile?.filename || 'Select action recording...'}
              </span>
              <span className="text-zinc-400 text-xs ml-1 flex-shrink-0">▼</span>
            </button>

            {showHarDropdown && (
          <div className="absolute top-full left-0 mt-2 w-96 bg-zinc-900 border border-white/10 rounded-xl shadow-xl shadow-black/50 z-[9999] max-h-96 overflow-hidden">
                <div className="bg-white/5 px-4 py-3 border-b border-white/10">
                  <span className="text-zinc-200 font-semibold text-sm">Action Recordings</span>
                </div>
                
                <div className="max-h-80 overflow-y-auto">
                  {availableHarFiles.length === 0 ? (
                   <div className="px-4 py-6 text-center text-zinc-500 text-sm">No action recordings available</div>
                  ) : (
                    availableHarFiles.map((file) => (
                      <div
                        key={file.filename}
                        className={`border-b border-white/5 last:border-b-0 ${
                          currentHarFile?.filename === file.filename ? 'bg-blue-500/20' : ''
                        }`}
                      >
                        {editingHarFile === file.filename ? (
                          <div className="p-3 space-y-2">
                            <input
                              type="text"
                              value={editHarFileName}
                              onChange={(e) => setEditHarFileName(e.target.value)}
                              className="w-full bg-zinc-800 border border-zinc-600 rounded px-2 py-1 text-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              placeholder="Enter filename (without extension)"
                              autoFocus
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') saveHarFileName();
                                if (e.key === 'Escape') cancelEditingHarFile();
                              }}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={saveHarFileName}
                                className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs transition-colors duration-200"
                              >
                                ✓ Save
                              </button>
                              <button
                                onClick={cancelEditingHarFile}
                                className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs transition-colors duration-200"
                              >
                                ✕ Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center p-3 hover:bg-white/5 transition-colors duration-200">
                            <div 
                              className="flex-1 min-w-0 cursor-pointer mr-2"
                              onClick={() => {
                                switchHarFile(file.filename);
                                setShowHarDropdown(false);
                              }}
                            >
                              <div className="text-zinc-200 text-sm font-medium truncate">{file.filename}</div>
                              <div className="text-zinc-400 text-xs">{formatFileSize(file.size)}</div>
                            </div>
                            
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingHarFile(file.filename);
                                }}
                                className="p-1 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/20 rounded transition-colors duration-200"
                                title="Rename file"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmFile(file.filename);
                                }}
                                className="p-1 text-zinc-400 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors duration-200"
                                title="Delete file"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* New Chat */}
          <div className="flex items-center ml-auto" style={{ gap: `${Math.max(6, chatWidth * 0.1)}px` }}>
            <button 
              className="h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 px-4"
              style={{
                minWidth: '80px',
                width: 'auto'
              }}
              onClick={startNewChatSession}
              disabled={isSwitchingChatSession}
              title="New Chat"
            >
              ➕ New Chat 
            </button>
            
            {/* Chat History Button hidden */}
            <div className="relative" style={{ zIndex: 99999 }} ref={chatHistoryRef}>
              <button 
                className="
                  bg-white/5 border border-white/10 rounded-lg
                  flex items-center justify-center text-zinc-200
                  transition-all duration-200 hover:bg-white/10 hover:border-white/20
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20 flex-shrink-0
                "
                style={{
                  width: '40px',
                  height: '40px',
                  fontSize: '16px'
                }}
                onClick={() => {
                  const newState = !showChatHistory;
                  setShowChatHistory(newState);
                  if (newState) {
                    loadChatSessions();
                  }
                }}
                title="Chat History"
              >
                🕐
              </button>
              
              {showChatHistory && (
                <div 
                  className="absolute top-full right-0 mt-2 min-w-[280px] max-w-[320px] bg-zinc-900 border border-white/10 rounded-xl shadow-xl shadow-black/50 z-[9999]"
                  style={{ zIndex: 99999, position: 'absolute' }}
                >
                  <div className="bg-white/5 px-4 py-3 border-b border-white/10 flex justify-between items-center">
                    <span className="text-zinc-200 font-semibold text-sm">Chat History</span>
                    <button 
                      className="
                        text-zinc-200 hover:text-white transition-colors duration-200
                        hover:bg-white/10 rounded p-1
                      "
                      onClick={loadChatSessions}
                      disabled={isLoadingChatSessions}
                      title="Refresh"
                    >
                      {isLoadingChatSessions ? '⏳' : '🔄'}
                    </button>
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto">
                    {chatSessions.length === 0 ? (
                      <div className="px-4 py-6 text-center text-zinc-500 text-sm">No chat history</div>
                    ) : (
                      chatSessions.map((session) => (
                        <button
                          key={session.session_id}
                          className={`
                            w-full px-4 py-3 text-left transition-all duration-200
                            border-b border-white/5 hover:bg-white/5 hover:pl-5
                            ${currentChatSession === session.session_id 
                              ? 'bg-blue-500/20 border-l-4 border-l-blue-500 pl-3' 
                              : 'hover:border-l-2 hover:border-l-blue-500/50'
                            }
                            disabled:cursor-not-allowed disabled:opacity-50
                          `}
                          onClick={() => switchChatSession(session.session_id)}
                          disabled={isSwitchingChatSession}
                        >
                          <div className="text-zinc-200 text-sm font-medium truncate">
                            {session.preview.substring(0, 40)}...
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  
                  {isSwitchingChatSession && (
                    <div className="px-4 py-3 text-center text-amber-500 text-sm bg-amber-500/10 border-t border-white/10 flex items-center justify-center gap-2">
                      🔄 Loading chat session...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {isSwitchingHarFile && (
          <div className="px-5 py-3 text-center text-amber-500 text-sm bg-gradient-to-r from-amber-500/10 to-amber-500/5 border-b border-amber-500/20 flex items-center justify-center gap-2 font-medium">
            🔄 Switching action recording and recreating agent...
          </div>
        )}

        <div style={{ flex: 1, position: 'relative', height: 0, overflow: 'visible', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, minHeight: 0 }}>
            <MainContainer style={{ height: '100%', overflow: 'visible' }}>
              <ChatContainer style={{ height: '100%', overflow: 'visible' }}>
                <MessageList
                  scrollBehavior="smooth"
                  style={{ height: 'calc(100% - 60px)' }}
                >
                  {messages.map((message) => {
                    let className = '';
                    if (message.sender === 'system') {
                      className = 'system-message';
                      if (message.messageType === 'tool_call') {
                        className += ' tool-call';
                      } else if (message.messageType === 'tool_response') {
                        className += ' tool-response';
                      }
                    }
                    
                    return (
                      <Message
                        key={message.id}
                        model={{
                          message: filterPythonCode(message.content),
                          sentTime: message.timestamp.toLocaleTimeString(),
                          sender: message.sender,
                          direction: message.direction,
                          position: "single"
                        }}
                        className={className}
                      />
                    );
                  })}
                  {isTyping && (
                    <Message
                      model={{
                        message: "",
                        sentTime: new Date().toLocaleTimeString(),
                        sender: "agent",
                        direction: "incoming",
                        position: "single"
                      }}
                    >
                      <Message.CustomContent>
                        <LoadingIndicator message={loadingMessage} />
                      </Message.CustomContent>
                    </Message>
                  )}
                </MessageList>

                <MessageInput
                  placeholder={!currentHarFile ? "Please select an action recording to start chatting..." : "Type your integration request here. (The more detailed the better)"}
                  value={currentMessage}
                  onChange={(val) => setCurrentMessage(val)}
                  onSend={handleSendMessage}
                  disabled={connectionStatus !== 'connected' || !currentHarFile}
                  attachButton={false}
                  style={{
                    opacity: !currentHarFile ? 0.5 : 1
                  }}
                />
              </ChatContainer>
            </MainContainer>
          </div>
          
        </div>
      </div>

      {/* Drag Handle between Chat and Main Content */}
      <div 
        className="drag-handle"
        onMouseDown={handleMouseDown}
      />

      {/* Main Content Section */}
      <div className="main-content-section bg-zinc-950" style={{ flex: 1 }}>
        {/* Live View Content */}
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 px-5 py-3 border-b border-zinc-800 flex items-center justify-between backdrop-blur-sm min-h-[64px]">
            <span className="text-zinc-200 font-semibold text-sm uppercase tracking-wide">Live View & Action Recordings</span>
            
            {/* Context Selector */}
            <div className="relative" ref={contextDropdownRef}>
              <button
                ref={contextButtonRef}
                className="
                  h-10 px-4 bg-white/5 border border-white/10 rounded-lg
                  text-zinc-200 text-sm font-medium cursor-pointer
                  transition-all duration-200 flex items-center gap-2
                  hover:bg-white/10 hover:border-white/20
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50
                  disabled:opacity-60 disabled:cursor-not-allowed
                "
                onClick={() => {
                  if (liveViewSession) return; // disabled safeguard
                  const newOpen = !showContextDropdown;
                  setShowContextDropdown(newOpen);
                  if (newOpen) {
                    loadSavedContexts();
                  }
                }}
                disabled={!!liveViewSession || isCreatingSession}
                title={liveViewSession ? 'Close the current Live View to choose a saved session' : 'Load saved browser context'}
              >
                <span>Saved Sessions</span>
                <span className="text-zinc-400 text-xs">▼</span>
              </button>

              {showContextDropdown && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-zinc-900 border border-white/10 rounded-xl shadow-xl shadow-black/50 z-[9999] max-h-96 overflow-hidden">
                  <div className="bg-white/5 px-4 py-3 border-b border-white/10">
                    <span className="text-zinc-200 font-semibold text-sm">🧠 Saved Browser Sessions</span>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {savedContexts.length === 0 ? (
                      <div className="px-4 py-6 text-center text-zinc-500 text-sm">
                        No saved contexts yet
                        <div className="text-xs text-zinc-600 mt-1">Close a live session to save one!</div>
                      </div>
                    ) : (
                      savedContexts.map((context) => {
                        const contextSummary = summarizeContextDetails(context);
                        return (
                          <div
                            key={context.id}
                            className="border-b border-white/5 last:border-b-0"
                          >
                            <button
                              className="w-full px-4 py-3 text-left transition-all duration-200 hover:bg-white/5"
                              onClick={() => createSessionWithContext(context.context_id, context.display_name)}
                              disabled={isCreatingSession}
                            >
                              <div
                                className="text-zinc-200 text-sm font-medium truncate"
                                title={context.context_id}
                              >
                                {context.context_id}
                              </div>
                              <div
                                className="text-zinc-400 text-xs mt-1 truncate"
                                title={context.display_name || context.context_name || ''}
                              >
                                {context.display_name || context.context_name || 'Unnamed context'}
                              </div>
                              {contextSummary && (
                                <div
                                  className="text-zinc-400 text-xs mt-1"
                                  title={contextSummary}
                                >
                                  {contextSummary}
                                </div>
                              )}
                              <div className="text-zinc-500 text-xs mt-1">
                                {new Date(context.created_at).toLocaleDateString()}
                              </div>
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Main content area */}
          <div className="flex-1 flex flex-col bg-zinc-950">
            {/* Live View iframe */}
            {liveViewSession && (
              <div className="flex-1 m-4 mb-0">
                <div className="h-full bg-white rounded-xl overflow-hidden shadow-2xl shadow-black/40 relative">
                  <iframe
                    src={liveViewSession.debug_url}
                    className="absolute inset-0 w-full h-full bg-white block"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    allow="clipboard-read; clipboard-write; camera; microphone"
                    title="Live Browser View"
                  />
                </div>
              </div>
            )}
            
            {!liveViewSession && (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center max-w-md">
                  <div className="text-6xl mb-6 opacity-50">🌐</div>
                  <div className="text-zinc-400 text-lg font-medium mb-3">No Active Session</div>
                  <div className="text-zinc-500 text-sm leading-relaxed">
                    Start a live browser session to record network traffic and interact with web applications in real-time.
                  </div>
                </div>
              </div>
            )}

                        {/* Live View Controls - Now at the bottom */}
            <div className="bg-gradient-to-t from-zinc-900 to-zinc-900/80 border-t border-zinc-700/50 backdrop-blur-sm">
              {!liveViewSession ? (
                <div className="p-4 h-[76px] flex items-center">
                  <button
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                    onClick={createLiveViewSession}
                    disabled={isCreatingSession || !conversationId}
                  >
                    {isCreatingSession ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Creating Session...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <span className="text-lg">🌐</span>
                        Start Live Browser Session
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                <div className="p-4 h-[76px] flex items-center justify-between">
                  {/* Session Info - Left Side */}
                  <div className="flex items-center gap-2 bg-gradient-to-r from-zinc-800/80 to-zinc-800/60 rounded-lg px-3 py-2.5 border border-zinc-700/50 backdrop-blur-sm">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <div className="text-zinc-200 text-xs font-medium">
                      Session: {liveViewSession.session_id}
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      liveViewSession.status === 'active' 
                        ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                        : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                    }`}>
                      {liveViewSession.status.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Control Buttons - Right Side */}
                  <div className="flex gap-3">
                    {/* If login flow mode is active, only show a single finish button */}
                    {loginFlowModeActive ? (
                      <>
                        <button
                          className={`bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center gap-2 ${loginRecordingStatus === 'stopping' ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''}`}
                          onClick={finishLoginFlow}
                          disabled={loginRecordingStatus === 'stopping'}
                          title="Stop recording and close the browser"
                        >
                          {loginRecordingStatus === 'stopping' ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                              Finishing...
                            </>
                          ) : (
                            <>
                              <span className="text-sm">✅</span>
                              Finish Recording Flow
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                    {harRecordingStatus === 'idle' && !isTerminatingSession && (
                      <button
                        className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center gap-2"
                        onClick={startHarRecording}
                      >
                        <span className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"></span>
                        Start Recording
                      </button>
                    )}
                    
                    {harRecordingStatus === 'starting' && (
                      <button className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-lg text-sm font-semibold cursor-not-allowed opacity-75 flex items-center gap-2" disabled>
                        <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                        Starting...
                      </button>
                    )}
                    
                    {harRecordingStatus === 'recording' && (
                      <button
                        className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center gap-2"
                        onClick={stopHarRecording}
                      >
                        <span className="w-2.5 h-2.5 bg-white rounded-sm"></span>
                        Stop Recording
                      </button>
                    )}
                    
                    {harRecordingStatus === 'stopping' && (
                      <button className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-lg text-sm font-semibold cursor-not-allowed opacity-75 flex items-center gap-2" disabled>
                        <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                        Stopping...
                      </button>
                    )}

                    {/* Login Flow Recorder Buttons removed from main page */}
                        <button
                          className={`bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center gap-2 ${(harRecordingStatus !== 'idle' || isTerminatingSession) ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''}`}
                          onClick={terminateLiveViewSession}
                          disabled={harRecordingStatus !== 'idle' || isTerminatingSession}
                          title={
                            isTerminatingSession ? 'Ending session...' :
                            harRecordingStatus !== 'idle' ? 'Cannot end session while action recording is active' : ''
                          }
                        >
                          {isTerminatingSession ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              Ending Session...
                            </>
                          ) : (
                            <>
                              <span className="text-sm">🛑</span>
                              End Session
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>


        </div>
      </div>

      {/* Tutorial overlay for main view */}
      {showArcadeOverlay && (
        <ArcadeOverlay
          onClose={() => setShowArcadeOverlay(false)}
          embedUrl="https://demo.arcade.software/R5Fr3xvJJ9AHwt7yxvnA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true"
          title="Create a One‑on‑One Meeting in Calendly and Generate an API Request"
        />
      )}

    </div>

      {/* Delete Action Recording Confirmation Dialog */}
      {deleteConfirmFile && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-zinc-900 border border-white/10 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="bg-white/5 px-6 py-4 border-b border-white/10">
              <h3 className="text-zinc-200 font-semibold text-lg">🗑️ Delete Action Recording</h3>
            </div>
            <div className="p-6">
              <p className="text-zinc-300 mb-4">
                Are you sure you want to delete <strong className="text-zinc-100">"{deleteConfirmFile}"</strong>?
              </p>
              <p className="text-zinc-400 text-sm mb-6">
                This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg transition-colors duration-200"
                  onClick={() => setDeleteConfirmFile(null)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors duration-200"
                  onClick={() => deleteHarFile(deleteConfirmFile)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Secret Key Manager Modal */}
      {showSecretKeyManager && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-zinc-900 border border-white/10 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="bg-white/5 px-6 py-4 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-zinc-200 font-semibold text-lg">🔑 API Key Management</h3>
              <button 
                className="text-zinc-400 hover:text-zinc-200 transition-colors duration-200 text-xl"
                onClick={() => setShowSecretKeyManager(false)}
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
              <SecretKeyManager />
            </div>
          </div>
        </div>
      )}

      {/* Action Recording Naming Dialog */}
      {showNamingDialog && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="naming-dialog" style={{ zIndex: 10000 }}>
            <div className="dialog-header">
              <h3>💾 Save or Discard Action Recording</h3>
            </div>
            <div className="dialog-content">
              <p>Give your action recording a descriptive name, or discard it:</p>
              <input
                type="text"
                className="file-name-input"
                value={newHarFileName}
                onChange={(e) => setNewHarFileName(e.target.value)}
                placeholder="Enter file name (without extension)"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newHarFileName.trim() && !isSavingHarFile && !isDiscardingHarFile) {
                    renameHarFile();
                  } else if (e.key === 'Escape') {
                    cancelNamingDialog();
                  }
                }}
              />
              <div className="dialog-preview">
                Will be saved as: <strong>{newHarFileName.trim()}.har</strong>
              </div>
              <div style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                Debug: newHarFileName="{newHarFileName}", lastCreatedHarFile="{lastCreatedHarFile}", 
                disabled={!newHarFileName.trim() ? 'true' : 'false'}
              </div>
            </div>
            <div className="dialog-actions">
              <button
                className="cancel-btn"
                onClick={cancelNamingDialog}
                disabled={isSavingHarFile || isDiscardingHarFile}
              >
                {isDiscardingHarFile ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <svg 
                      width="16" 
                      height="16" 
                      fill="currentColor" 
                      className="animate-spin" 
                      viewBox="0 0 16 16"
                    >
                      <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                      <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"/>
                    </svg>
                    Discarding...
                  </span>
                ) : (
                  "🗑️ Discard Recording"
                )}
              </button>
              <button 
                className="save-btn"
                onClick={() => {
                  console.log('Save button clicked!', { newHarFileName, disabled: !newHarFileName.trim() });
                  renameHarFile();
                }}
                disabled={!newHarFileName.trim() || isSavingHarFile || isDiscardingHarFile}
              >
                {isSavingHarFile ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <svg 
                      style={{ animation: 'spin 1s linear infinite', width: '14px', height: '14px' }} 
                      viewBox="0 0 24 24"
                    >
                      <circle 
                        cx="12" 
                        cy="12" 
                        r="10" 
                        stroke="currentColor" 
                        strokeWidth="4" 
                        fill="none"
                        style={{ opacity: 0.25 }}
                      />
                      <path 
                        fill="currentColor" 
                        style={{ opacity: 0.75 }}
                        d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  '💾 Save'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Naming Dialog */}
      {showContextNamingDialog && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-zinc-900 border border-white/10 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="bg-white/5 px-6 py-4 border-b border-white/10">
              <h3 className="text-zinc-200 font-semibold text-lg">Save Authentication Information</h3>
            </div>
            <div className="p-6">
              <p className="text-zinc-300 mb-4">
                Choose a name for this website session.
              </p>
              <input
                type="text"
                value={newContextName}
                onChange={(e) => setNewContextName(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Shopping Cart Flow, Login Testing, etc."
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newContextName.trim() && !isCancelingContext && !isSavingContext) {
                    saveContext();
                  } else if (e.key === 'Escape') {
                    cancelContextSaving();
                  }
                }}
              />
              <div className="text-zinc-400 text-sm mt-3">
                If your task to integrate requires any form of authentication (i.e. login), the generated API endpoint will require cookies or some form of authentication tokens. Create a unique name for this task that will help you retrieve the auth information automatically when you need it from the cookies tab.
              </div>
            </div>
            <div className="flex gap-3 justify-end p-6 pt-0">
              <button
                className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={cancelContextSaving}
                disabled={isCancelingContext || isSavingContext}
              >
                {isCancelingContext ? (
                  <span className="flex items-center gap-2">
                    <svg 
                      className="animate-spin w-4 h-4" 
                      fill="none" 
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Canceling...
                  </span>
                ) : (
                  "Cancel"
                )}
              </button>
              <button
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={saveContext}
                disabled={!newContextName.trim() || isCancelingContext || isSavingContext}
              >
                {isSavingContext ? (
                  <span className="flex items-center gap-2">
                    <svg 
                      className="animate-spin w-4 h-4" 
                      fill="none" 
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  "💾 Save Context"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-Save: Record Login Flow Prompt removed */}

      {/* Route Creation Popup */}
      {(showRoutePopup && routePopupData) && (
        <RouteDetailsModal
          isOpen={true}
          onClose={() => {
            setShowRoutePopup(false);
            setRoutePopupData(null);
          }}
          routeInfo={routePopupData?.route_info || {name: "test", methods: ["GET"], endpoint_url: "/test"}}
          curlExamples={routePopupData?.curl_examples}
          isNewRoute={true}
        />
      )}

      {/* FAQ Popup */}
      <FAQPopup 
        isOpen={showFAQPopup} 
        onClose={() => setShowFAQPopup(false)} 
      />
    </div>
  );
};

export default App; 
